import { useEffect, useRef, useState, useCallback } from 'react';
import { toast } from 'sonner';
import { Plus, Minus, ArrowLeftRight, ScanLine, Download, Scale, Ban, Pencil, Camera, Eye, Trash2, ReceiptText, CalendarCheck, Send } from 'lucide-react';
import api, { apiError } from '../lib/api';
import { formatIDR, formatDate, todayStr } from '../lib/format';
import { MoneyInput } from '../components/MoneyInput';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from '../components/ui/alert-dialog';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { HapusCari } from '../components/HapusCari';
import { KasPerAkun } from '../components/KasPerAkun';
import { TotalBar } from '../components/TotalBar';
import { susunTotalKas } from '../lib/totalBar';
import { OUT_CATS, AkunOptions, infoAkun } from '../lib/akunKas';

// Sumber dana = TEMPAT uang berada. Metode = CARA uang berpindah.
/* Menyorot kata yang dicari di dalam teks (pencarian di menu Kas).
   Mengembalikan potongan teks biasa + <mark>; aman karena tidak memakai HTML mentah. */
function Tandai({ teks, kata }) {
  const t = String(teks ?? '');
  const daftar = (kata || []).filter(Boolean);
  if (!t || daftar.length === 0) return t;
  const pola = new RegExp(`(${daftar.map((k) => k.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')).join('|')})`, 'ig');
  return t.split(pola).map((bagian, i) => (
    i % 2 === 1 ? <mark key={i} className="bg-amber-200/70 rounded px-0.5 text-inherit">{bagian}</mark> : bagian
  ));
}

const SOURCE_L = { cash: 'Tunai (di laci)', bank_ops: 'Rekening Operasional', bank_utama: 'Rekening Bisnis Utama' };
const SOURCE_SHORT = { cash: 'Tunai', bank_ops: 'Rek. Operasional', bank_utama: 'Rek. Bisnis Utama' };
const SOURCE_TAG = {
  cash: 'bg-caramel/15 text-brown-med',
  bank_ops: 'bg-success/12 text-success',
  bank_utama: 'bg-gold/20 text-gold-dark',
};
const SOURCE_DOT = { cash: 'bg-caramel', bank_ops: 'bg-success', bank_utama: 'bg-gold' };

function SrcTag({ s }) {
  if (!s) return <span className="text-mocha">-</span>;
  return <span className={`inline-block rounded-md px-1.5 py-0.5 text-[10px] font-bold ${SOURCE_TAG[s] || 'bg-ivory text-mocha'}`}>{SOURCE_SHORT[s] || s}</span>;
}
const METHOD_L = { '': '— tidak dicatat —', cash: 'Tunai', transfer: 'Transfer', qris: 'QRIS', debit: 'Kartu Debit', lainnya: 'Lainnya' };
// Harus sama dengan APP_BUILD di backend/server.py.
const BUILD_HARAPAN = '2026-11-01.04';

const CAT_PRIVE = 'Prive / Pemakaian Pribadi';
const CAT_UTANG = 'Utang Pemilik';
const CAT_BAYAR_UTANG = 'Pembayaran Utang Pemilik';
const CAT_PINJAMAN_KEMBALI = 'Pengembalian Pinjaman';
const IN_CATS = ['Pembayaran DP Customer', 'Pelunasan Customer', 'Transfer Masuk', 'Setor Tunai', CAT_BAYAR_UTANG, CAT_PINJAMAN_KEMBALI, 'Pendapatan Lain', 'Lainnya'];
// Akun pengeluaran (CoA) ada di lib/akunKas.jsx — satu daftar untuk semua halaman.

// Foto dikecilkan dulu di browser supaya database tidak membengkak.
const compressImage = (file, maxSide = 1600, quality = 0.72) =>
  new Promise((resolve, reject) => {
    const img = new window.Image();
    const url = URL.createObjectURL(file);
    img.onload = () => {
      const s = Math.min(1, maxSide / Math.max(img.width, img.height));
      const c = document.createElement('canvas');
      c.width = Math.round(img.width * s);
      c.height = Math.round(img.height * s);
      c.getContext('2d').drawImage(img, 0, 0, c.width, c.height);
      URL.revokeObjectURL(url);
      resolve(c.toDataURL('image/jpeg', quality).split(',')[1]);
    };
    img.onerror = (e) => { URL.revokeObjectURL(url); reject(e); };
    img.src = url;
  });

// Cadangan kalau pengecilan di browser gagal — terutama foto bawaan iPhone
// (HEIC), yang sama sekali tidak bisa dibaca canvas. Berkas aslinya dikirim apa
// adanya; mesin aplikasi yang membaca, memutar, dan mengecilkan.
const bacaMentah = (file) =>
  new Promise((resolve, reject) => {
    const fr = new FileReader();
    fr.onload = () => resolve(String(fr.result || '').split(',')[1] || '');
    fr.onerror = reject;
    fr.readAsDataURL(file);
  });

function NotaCapture({ value, onChange, testid = 'nota' }) {
  const [camOn, setCamOn] = useState(false);
  const videoRef = useRef(null);
  const streamRef = useRef(null);
  const fileRef = useRef(null);

  const stopCam = useCallback(() => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((t) => t.stop());
      streamRef.current = null;
    }
    setCamOn(false);
  }, []);
  useEffect(() => () => stopCam(), [stopCam]);

  const startCam = async () => {
    try {
      const s = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: { ideal: 'environment' }, width: { ideal: 1920 } },
      });
      streamRef.current = s;
      setCamOn(true);
      setTimeout(() => {
        if (videoRef.current) { videoRef.current.srcObject = s; videoRef.current.play().catch(() => {}); }
      }, 60);
    } catch {
      toast.error('Kamera tidak bisa dibuka. Pakai tombol "Dari File" untuk memilih foto nota.');
    }
  };

  const shoot = () => {
    const v = videoRef.current;
    if (!v || !v.videoWidth) return;
    const s = Math.min(1, 1600 / Math.max(v.videoWidth, v.videoHeight));
    const c = document.createElement('canvas');
    c.width = Math.round(v.videoWidth * s);
    c.height = Math.round(v.videoHeight * s);
    c.getContext('2d').drawImage(v, 0, 0, c.width, c.height);
    onChange(c.toDataURL('image/jpeg', 0.72).split(',')[1]);
    stopCam();
  };

  const pick = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    try { onChange(await compressImage(f)); }
    catch { toast.error('Gagal membaca foto.'); }
    finally { if (fileRef.current) fileRef.current.value = ''; }
  };

  if (value) {
    return (
      <div className="rounded-xl border border-border bg-white p-2" data-testid={`${testid}-preview`}>
        <img src={`data:image/jpeg;base64,${value}`} alt="Nota" className="w-full max-h-52 object-contain rounded-lg" />
        <div className="flex gap-2 mt-2">
          <button type="button" onClick={() => onChange('')} data-testid={`${testid}-clear`}
            className="flex-1 inline-flex items-center justify-center gap-1.5 rounded-lg border border-border py-2 text-xs font-bold text-mocha hover:bg-ivory">
            <Trash2 size={13} /> Ganti Foto
          </button>
        </div>
      </div>
    );
  }

  if (camOn) {
    return (
      <div className="rounded-xl border border-border bg-black p-2" data-testid={`${testid}-camera`}>
        <video ref={videoRef} playsInline muted className="w-full max-h-56 object-contain rounded-lg bg-black" />
        <div className="flex gap-2 mt-2">
          <button type="button" onClick={shoot} data-testid={`${testid}-shoot`}
            className="flex-1 rounded-lg bg-brown text-white py-2 text-xs font-bold hover:bg-brown-med">Jepret</button>
          <button type="button" onClick={stopCam}
            className="rounded-lg bg-white/90 px-3 py-2 text-xs font-bold text-ink">Batal</button>
        </div>
      </div>
    );
  }

  return (
    <div className="flex gap-2">
      <button type="button" onClick={startCam} data-testid={`${testid}-open-cam`}
        className="flex-1 inline-flex items-center justify-center gap-1.5 rounded-xl border border-dashed border-brown/50 bg-white py-2.5 text-xs font-bold text-brown hover:bg-ivory">
        <Camera size={14} /> Buka Kamera
      </button>
      <button type="button" onClick={() => fileRef.current?.click()} data-testid={`${testid}-open-file`}
        className="flex-1 inline-flex items-center justify-center gap-1.5 rounded-xl border border-dashed border-brown/50 bg-white py-2.5 text-xs font-bold text-brown hover:bg-ivory">
        <ReceiptText size={14} /> Dari File
      </button>
      {/* Tanpa atribut capture. Dengan capture, tombol "Dari File" malah membuka
          kamera dan galeri tidak pernah terbuka — dilaporkan 20 Okt 2026. */}
      <input ref={fileRef} type="file" accept="image/*" onChange={pick} className="hidden" data-testid={`${testid}-file`} />
    </div>
  );
}

const TEMPAT = [
  { key: 'cash', note: 'Belum disetor ke rekening. Angka ini harus sama dengan uang fisik yang kamu hitung.' },
  { key: 'bank_ops', note: 'Semua transfer customer & setoran tunai masuk sini. Semua pengeluaran keluar dari sini.' },
  { key: 'bank_utama', note: 'Simpanan dana bisnis. Ditarik ke operasional saat kurang, diisi balik saat ada sisa.' },
];

const NAMA_BULAN = ['Jan', 'Feb', 'Mar', 'Apr', 'Mei', 'Jun', 'Jul', 'Agu', 'Sep', 'Okt', 'Nov', 'Des'];
const AKHIR_BULAN = (ym) => {
  const [y, m] = (ym || '').split('-').map(Number);
  if (!y || !m) return '';
  return `${y}-${String(m).padStart(2, '0')}-${String(new Date(y, m, 0).getDate()).padStart(2, '0')}`;
};
const BULAN_ID = (ym) => {
  const [y, m] = (ym || '').split('-');
  const i = parseInt(m, 10) - 1;
  return NAMA_BULAN[i] ? `${NAMA_BULAN[i]} ${y}` : ym;
};

function EntryDialog({ open, onClose, direction, onSaved }) {
  const isIn = direction === 'in';
  const cats = isIn ? IN_CATS : OUT_CATS;
  const [form, setForm] = useState({ category: cats[0], source: 'bank_ops', method: '', amount: '', date: '', customer_name: '', order_number: '', notes: '' });
  const [dupFor, setDupFor] = useState(null);
  const [nota, setNota] = useState('');
  const [alasan, setAlasan] = useState('');
  // Saran akun dari keterangan. Akun diisi otomatis selama belum dipilih sendiri.
  const [saran, setSaran] = useState(null);
  const [akunDipilih, setAkunDipilih] = useState(false);

  useEffect(() => {
    if (open) {
      const c = direction === 'in' ? IN_CATS : OUT_CATS;
      setForm({ category: c[0], source: 'bank_ops', method: '', amount: '', date: '', customer_name: '', order_number: '', notes: '' });
      setNota(''); setAlasan(''); setSaran(null); setAkunDipilih(false);
    }
  }, [open, direction]);

  useEffect(() => {
    if (isIn || !open) return undefined;
    const teks = form.notes.trim();
    if (teks.length < 3) { setSaran(null); return undefined; }
    const t = setTimeout(() => {
      api.get('/kas/saran-akun', { params: { teks, nominal: parseFloat(form.amount) || 0 } })
        .then((r) => {
          const s = r.data?.saran || null;
          setSaran(s);
          if (s && !akunDipilih) setForm((f) => ({ ...f, category: s.nama }));
        })
        .catch(() => setSaran(null));
    }, 400);
    return () => clearTimeout(t);
  }, [form.notes, form.amount, isIn, open, akunDipilih]);

  // Setiap pengeluaran harus punya bukti: foto nota, atau alasan tertulis kenapa tidak ada.
  const tanpaNotaWajar = [CAT_PRIVE, CAT_UTANG].includes(form.category);
  const buktiKurang = !isIn && !nota && !alasan.trim() && !tanpaNotaWajar;

  const submit = async (confirmDuplicate = false) => {
    try {
      await api.post('/kas/entry', {
        ...form, direction, amount: parseFloat(form.amount) || 0, confirm_duplicate: confirmDuplicate,
        image_base64: nota || undefined, no_receipt_reason: nota ? '' : alasan.trim(),
      });
      toast.success('Transaksi kas berhasil dicatat — saldo diperbarui otomatis.');
      setDupFor(null);
      onClose();
      onSaved();
    } catch (err) {
      if (err.response?.status === 409) setDupFor(form);
      else toast.error(apiError(err));
    }
  };

  return (
    <>
      <Dialog open={open} onOpenChange={onClose}>
        <DialogContent data-testid={`kas-${direction}-dialog`} className="bg-ivory max-w-md">
          <DialogHeader>
            <DialogTitle className="font-display text-brown-dark">{isIn ? '+ Uang Masuk' : '+ Pengeluaran'}</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-2">
              <div>
                <label className="text-[11px] uppercase tracking-wider text-mocha font-semibold">Tanggal</label>
                <input data-testid="kas-entry-date" type="date" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })}
                  className="mt-1 w-full rounded-xl border border-input bg-white px-3 py-2.5 text-sm outline-none focus:ring-2 focus:ring-brown/30" />
              </div>
              <div>
                <label className="text-[11px] uppercase tracking-wider text-mocha font-semibold">Sumber Dana</label>
                <select data-testid="kas-entry-source" value={form.source} onChange={(e) => setForm({ ...form, source: e.target.value })}
                  className="mt-1 w-full rounded-xl border border-input bg-white px-3 py-2.5 text-sm outline-none focus:ring-2 focus:ring-brown/30">
                  {Object.entries(SOURCE_L).map(([v, l]) => <option key={v} value={v}>{l}</option>)}
                </select>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-2">
              <div className={isIn ? '' : 'col-span-2'}>
                <label className="text-[11px] uppercase tracking-wider text-mocha font-semibold">{isIn ? 'Kategori' : 'Akun'}</label>
                <select data-testid="kas-entry-category" value={form.category}
                  onChange={(e) => { setForm({ ...form, category: e.target.value }); if (!isIn) setAkunDipilih(true); }}
                  className="mt-1 w-full rounded-xl border border-input bg-white px-3 py-2.5 text-sm outline-none focus:ring-2 focus:ring-brown/30">
                  {isIn ? cats.map((c) => <option key={c} value={c}>{c}</option>) : <AkunOptions />}
                </select>
              </div>
              <div>
                <label className="text-[11px] uppercase tracking-wider text-mocha font-semibold">Cara Bayar</label>
                <select data-testid="kas-entry-method" value={form.method} onChange={(e) => setForm({ ...form, method: e.target.value })}
                  className="mt-1 w-full rounded-xl border border-input bg-white px-3 py-2.5 text-sm outline-none focus:ring-2 focus:ring-brown/30">
                  {Object.entries(METHOD_L).map(([v, l]) => <option key={v} value={v}>{l}</option>)}
                </select>
                <p className="text-[10px] text-mocha mt-1 leading-snug">Catatan saja. Tidak mengubah saldo.</p>
              </div>
            </div>
            <div>
              <label className="text-[11px] uppercase tracking-wider text-mocha font-semibold">Nominal (Rp)</label>
              <MoneyInput data-testid="kas-entry-amount" value={form.amount} onChange={(v) => setForm({ ...form, amount: v })}
                className="mt-1 w-full rounded-xl border border-input bg-white px-3 py-2.5 text-sm outline-none focus:ring-2 focus:ring-brown/30" placeholder="0" />
            </div>
            {isIn && (
              <div className="grid grid-cols-2 gap-2">
                <input data-testid="kas-entry-customer" value={form.customer_name} onChange={(e) => setForm({ ...form, customer_name: e.target.value })} placeholder="Customer (opsional)"
                  className="rounded-xl border border-input bg-white px-3 py-2.5 text-sm outline-none focus:ring-2 focus:ring-brown/30" />
                <input data-testid="kas-entry-order" value={form.order_number} onChange={(e) => setForm({ ...form, order_number: e.target.value })} placeholder="ID Order (opsional)"
                  className="rounded-xl border border-input bg-white px-3 py-2.5 text-sm outline-none focus:ring-2 focus:ring-brown/30" />
              </div>
            )}
            <input data-testid="kas-entry-notes" value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })}
              placeholder={isIn ? 'Keterangan' : 'Keterangan / nama toko — mis. token PLN, beli Molto, ongkir kebaya'}
              className="w-full rounded-xl border border-input bg-white px-3 py-2.5 text-sm outline-none focus:ring-2 focus:ring-brown/30" />
            {!isIn && saran && (
              <p data-testid="kas-entry-saran" className="text-[11px] leading-snug rounded-lg bg-sky-50 border border-sky-100 px-2.5 py-1.5 -mt-1">
                Saran akun: <b>{saran.nama}</b>
                {form.category !== saran.nama && (
                  <button type="button" data-testid="kas-entry-saran-pakai"
                    onClick={() => { setForm({ ...form, category: saran.nama }); setAkunDipilih(true); }}
                    className="ml-2 underline font-semibold text-brown">pakai</button>
                )}
              </p>
            )}

            {!isIn && !tanpaNotaWajar && (
              <div className="rounded-xl border border-border bg-white/60 p-3 space-y-2">
                <p className="text-[11px] uppercase tracking-wider text-mocha font-semibold">Foto Nota — wajib</p>
                <NotaCapture value={nota} onChange={setNota} testid="kas-entry-nota" />
                {!nota && (
                  <>
                    <input data-testid="kas-entry-alasan" value={alasan} onChange={(e) => setAlasan(e.target.value)}
                      placeholder="Kalau nota memang tidak ada, tulis alasannya di sini"
                      className="w-full rounded-xl border border-input bg-white px-3 py-2 text-xs outline-none focus:ring-2 focus:ring-brown/30" />
                    <p className="text-[10px] text-mocha leading-snug">
                      Contoh: transfer bank tanpa struk, ongkos parkir, bayar tukang. Alasan ini ikut tersimpan supaya pembukuan tetap bisa ditelusuri.
                    </p>
                  </>
                )}
              </div>
            )}

            <button data-testid="kas-entry-submit" onClick={() => submit(false)} disabled={buktiKurang}
              className={`w-full rounded-xl text-white py-3 text-sm font-bold disabled:opacity-50 disabled:cursor-not-allowed ${isIn ? 'bg-success hover:bg-success/90' : 'bg-danger hover:bg-danger/90'}`}>
              Konfirmasi {isIn ? 'Uang Masuk' : 'Pengeluaran'}
            </button>
          </div>
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!dupFor} onOpenChange={() => setDupFor(null)}>
        <AlertDialogContent data-testid="kas-dup-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle className="font-display text-brown-dark">Kemungkinan Duplikat</AlertDialogTitle>
            <AlertDialogDescription>Ada transaksi dengan tanggal, nominal, dan kategori yang sama. Tetap simpan?</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="kas-dup-cancel">Batal</AlertDialogCancel>
            <AlertDialogAction data-testid="kas-dup-confirm" onClick={() => submit(true)} className="bg-brown hover:bg-brown-med">Tetap Simpan</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}

function TransferDialog({ open, onClose, onSaved }) {
  const [form, setForm] = useState({ from_source: 'cash', to_source: 'bank_ops', amount: '', date: '', notes: '' });
  useEffect(() => { if (open) setForm({ from_source: 'cash', to_source: 'bank_ops', amount: '', date: '', notes: '' }); }, [open]);
  const submit = async () => {
    try {
      await api.post('/kas/transfer', { ...form, amount: parseFloat(form.amount) || 0 });
      toast.success('Transfer antar kas berhasil — total dana bisnis tidak berubah.');
      onClose();
      onSaved();
    } catch (err) { toast.error(apiError(err)); }
  };
  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent data-testid="kas-transfer-dialog" className="bg-ivory max-w-md">
        <DialogHeader><DialogTitle className="font-display text-brown-dark">Transfer Antar Kas</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div className="grid grid-cols-2 gap-2">
            <div>
              <label className="text-[11px] uppercase tracking-wider text-mocha font-semibold">Dari</label>
              <select data-testid="kas-transfer-from" value={form.from_source} onChange={(e) => setForm({ ...form, from_source: e.target.value })}
                className="mt-1 w-full rounded-xl border border-input bg-white px-3 py-2.5 text-sm outline-none">
                {Object.entries(SOURCE_L).map(([v, l]) => <option key={v} value={v}>{l}</option>)}
              </select>
            </div>
            <div>
              <label className="text-[11px] uppercase tracking-wider text-mocha font-semibold">Ke</label>
              <select data-testid="kas-transfer-to" value={form.to_source} onChange={(e) => setForm({ ...form, to_source: e.target.value })}
                className="mt-1 w-full rounded-xl border border-input bg-white px-3 py-2.5 text-sm outline-none">
                {Object.entries(SOURCE_L).map(([v, l]) => <option key={v} value={v}>{l}</option>)}
              </select>
            </div>
          </div>
          <MoneyInput data-testid="kas-transfer-amount" value={form.amount} onChange={(v) => setForm({ ...form, amount: v })} placeholder="Nominal (Rp)"
            className="w-full rounded-xl border border-input bg-white px-3 py-2.5 text-sm outline-none focus:ring-2 focus:ring-brown/30" />
          <input data-testid="kas-transfer-date" type="date" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })}
            className="w-full rounded-xl border border-input bg-white px-3 py-2.5 text-sm outline-none" />
          <input data-testid="kas-transfer-notes" value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} placeholder="Keterangan"
            className="w-full rounded-xl border border-input bg-white px-3 py-2.5 text-sm outline-none" />
          <button data-testid="kas-transfer-submit" onClick={submit}
            className="w-full rounded-xl bg-brown text-white py-3 text-sm font-bold hover:bg-brown-med">Konfirmasi Transfer</button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function ScanDialog({ open, onClose, mode, onSaved }) {
  const [busy, setBusy] = useState(false);
  const [parsed, setParsed] = useState(null);
  const [candidates, setCandidates] = useState([]);
  const [editForm, setEditForm] = useState(null);
  const [rawImage, setRawImage] = useState('');
  const fileRef = useRef(null);
  const isNota = mode === 'nota';

  useEffect(() => { if (open) { setParsed(null); setCandidates([]); setEditForm(null); setRawImage(''); } }, [open]);

  const onFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setBusy(true);
    const tid = toast.loading('AI sedang membaca gambar…');
    try {
      // HEIC gagal di canvas -> kirim berkas asli, mesin aplikasi yang mengolah.
      const b64 = await compressImage(file).catch(() => bacaMentah(file));
      setRawImage(b64);
      // Jalur pengeluaran menerima nota kertas, bukti transfer keluar, maupun QRIS.
      const r = await api.post(isNota ? '/kas/scan-pengeluaran' : '/kas/scan-transfer', { image_base64: b64, mime_type: 'image/jpeg' });
      const d = r.data.parsed || {};
      setParsed(d);
      setCandidates(r.data.candidates || []);
      if (isNota) {
        setEditForm({
          date: d.tanggal || '',
          amount: d.nominal ?? d.total ?? '',
          category: d.saran_kategori || 'Lainnya',
          source: d.saran_sumber || 'bank_ops',
          notes: [d.penerima || d.toko, d.keterangan,
                  (d.items || []).map((i) => i.nama).join(', ')]
                 .filter(Boolean).join(' — '),
        });
      }
      toast.success('Data berhasil dibaca — periksa sebelum konfirmasi.', { id: tid });
    } catch (err) {
      toast.error(apiError(err, 'Gagal membaca gambar'), { id: tid });
    } finally {
      setBusy(false);
      if (fileRef.current) fileRef.current.value = '';
    }
  };

  const confirmNota = async () => {
    try {
      await api.post('/kas/entry', {
        direction: 'out', category: editForm.category, source: editForm.source,
        amount: parseFloat(editForm.amount) || 0, date: editForm.date || undefined, notes: editForm.notes,
        image_base64: rawImage || undefined,
      });
      toast.success('Pengeluaran dari nota dikonfirmasi — foto nota ikut tersimpan.');
      onClose();
      onSaved();
    } catch (err) { toast.error(apiError(err)); }
  };

  const confirmTransfer = async (cand) => {
    try {
      await api.post(`/transactions/${cand.transaction_id}/payments`, {
        amount: parseFloat(parsed.nominal) || cand.remaining, method: 'transfer',
        date: parsed.tanggal || undefined, kind: undefined,
      });
      toast.success(`Pembayaran ${cand.transaction_number} tercatat — masuk Laporan Pembayaran & kas Bank.`);
      onClose();
      onSaved();
    } catch (err) { toast.error(apiError(err)); }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent data-testid={`scan-${mode}-dialog`} className="bg-ivory max-w-md max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="font-display text-brown-dark">{isNota ? '📷 Scan Nota' : '📷 Scan Bukti Transfer'}</DialogTitle>
        </DialogHeader>
        <input ref={fileRef} data-testid={`scan-${mode}-file`} type="file" accept="image/*" onChange={onFile}
          className="w-full rounded-xl border border-dashed border-brown/40 bg-white px-3 py-3 text-sm" />
        {busy && <p className="text-xs text-mocha">Membaca gambar dengan AI…</p>}
        {parsed && (
          <div className="space-y-3">
            <pre data-testid={`scan-${mode}-parsed`} className="bg-white rounded-xl border border-border p-3 text-xs whitespace-pre-wrap">{JSON.stringify(parsed, null, 2)}</pre>
            {isNota && editForm && (
              <div className="space-y-2">
                <p className="text-xs font-bold text-brown-dark">Periksa & edit sebelum konfirmasi:</p>
                <div className="grid grid-cols-2 gap-2">
                  <input data-testid="scan-nota-date" type="date" value={editForm.date} onChange={(e) => setEditForm({ ...editForm, date: e.target.value })}
                    className="rounded-xl border border-input bg-white px-3 py-2 text-sm outline-none" />
                  <MoneyInput data-testid="scan-nota-amount" value={editForm.amount} onChange={(v) => setEditForm({ ...editForm, amount: v })}
                    className="rounded-xl border border-input bg-white px-3 py-2 text-sm outline-none" placeholder="Total (Rp)" />
                  <select data-testid="scan-nota-category" value={editForm.category} onChange={(e) => setEditForm({ ...editForm, category: e.target.value })}
                    className="col-span-2 order-last rounded-xl border border-input bg-white px-3 py-2 text-sm outline-none">
                    <AkunOptions />
                  </select>
                  <select data-testid="scan-nota-source" value={editForm.source} onChange={(e) => setEditForm({ ...editForm, source: e.target.value })}
                    className="rounded-xl border border-input bg-white px-3 py-2 text-sm outline-none">
                    {Object.entries(SOURCE_L).map(([v, l]) => <option key={v} value={v}>{l}</option>)}
                  </select>
                </div>
                {parsed?.akun_perlu_cek && (
                  <p data-testid="scan-nota-akun-ragu" className="text-[11px] rounded-lg bg-amber-50 border border-amber-200 px-2.5 py-1.5 text-amber-900">
                    ⚠️ AI belum yakin dengan akun <b>{editForm.category}</b>. Periksa sebelum konfirmasi.
                  </p>
                )}
                <input data-testid="scan-nota-notes" value={editForm.notes} onChange={(e) => setEditForm({ ...editForm, notes: e.target.value })}
                  className="w-full rounded-xl border border-input bg-white px-3 py-2 text-sm outline-none" placeholder="Keterangan" />
                <button data-testid="scan-nota-confirm" onClick={confirmNota}
                  className="w-full rounded-xl bg-danger text-white py-3 text-sm font-bold hover:bg-danger/90">Konfirmasi Pengeluaran</button>
              </div>
            )}
            {!isNota && (
              <div className="space-y-2">
                <p className="text-xs font-bold text-brown-dark">Kemungkinan customer/order yang cocok:</p>
                {candidates.length === 0 && <p className="text-xs text-mocha">Tidak ditemukan order dengan sisa tagihan yang cocok.</p>}
                {candidates.map((c) => (
                  <div key={c.transaction_id} data-testid={`scan-tf-cand-${c.transaction_number}`}
                    className="bg-white rounded-xl border border-border p-3 flex items-center justify-between gap-2">
                    <div>
                      <p className="text-sm font-bold text-brown">{c.transaction_number} — {c.customer_name}</p>
                      <p className="text-xs text-mocha">Sisa tagihan {formatIDR(c.remaining)}</p>
                    </div>
                    <button data-testid={`scan-tf-confirm-${c.transaction_number}`} onClick={() => confirmTransfer(c)}
                      className="rounded-xl bg-success text-white px-3 py-2 text-xs font-bold hover:bg-success/90 shrink-0">Konfirmasi</button>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

function EditEntryDialog({ entry, onClose, onSaved }) {
  const [form, setForm] = useState(null);
  useEffect(() => {
    if (!entry) { setForm(null); return; }
    setForm({
      date: entry.date || '', category: entry.category || '', source: entry.source || 'bank_ops',
      method: entry.method || '', amount: String(entry.amount ?? ''),
      customer_name: entry.customer_name || '', order_number: entry.order_number || '',
      notes: entry.notes || '',
    });
  }, [entry]);

  if (!entry || !form) return null;
  const isIn = entry.direction === 'in';
  const isTransfer = entry.direction === 'transfer';
  const cats = isIn ? IN_CATS : OUT_CATS;
  const catList = cats.includes(form.category) ? cats : [form.category, ...cats];

  const submit = async () => {
    try {
      const r = await api.patch(`/kas/entry/${entry.id}`, { ...form, amount: parseFloat(form.amount) || 0 });
      const n = Object.keys(r.data.changed || {}).length;
      toast.success(n === 0 ? 'Tidak ada yang berubah.' : `Transaksi dikoreksi (${n} kolom). Riwayat perubahan tersimpan.`);
      onClose(); onSaved();
    } catch (err) { toast.error(apiError(err)); }
  };

  return (
    <Dialog open={!!entry} onOpenChange={onClose}>
      <DialogContent data-testid="kas-edit-dialog" className="bg-ivory max-w-md max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="font-display text-brown-dark">Koreksi Transaksi Kas</DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          <p className="text-[11px] text-mocha leading-snug bg-white rounded-xl border border-border p-2.5">
            Nilai lama tetap tersimpan sebagai riwayat. Baris ini tidak perlu di-void.
          </p>
          <div className="grid grid-cols-2 gap-2">
            <div>
              <label className="text-[11px] uppercase tracking-wider text-mocha font-semibold">Tanggal</label>
              <input data-testid="kas-edit-date" type="date" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })}
                className="mt-1 w-full rounded-xl border border-input bg-white px-3 py-2.5 text-sm outline-none focus:ring-2 focus:ring-brown/30" />
            </div>
            <div>
              <label className="text-[11px] uppercase tracking-wider text-mocha font-semibold">Nominal (Rp)</label>
              <MoneyInput data-testid="kas-edit-amount" value={form.amount} onChange={(v) => setForm({ ...form, amount: v })}
                className="mt-1 w-full rounded-xl border border-input bg-white px-3 py-2.5 text-sm outline-none focus:ring-2 focus:ring-brown/30" />
            </div>
          </div>
          {!isTransfer && (
            <div className="grid grid-cols-2 gap-2">
              <div className={isIn ? '' : 'col-span-2'}>
                <label className="text-[11px] uppercase tracking-wider text-mocha font-semibold">{isIn ? 'Kategori' : 'Akun'}</label>
                <select data-testid="kas-edit-category" value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })}
                  className="mt-1 w-full rounded-xl border border-input bg-white px-3 py-2.5 text-sm outline-none focus:ring-2 focus:ring-brown/30">
                  {isIn ? catList.map((c) => <option key={c} value={c}>{c}</option>) : <AkunOptions tambahan={form.category} />}
                </select>
                {!isIn && (entry.akun_perlu_cek || infoAkun(form.category).lama) && (
                  <p data-testid="kas-edit-akun-cek" className="text-[10px] text-amber-800 mt-1 leading-snug">
                    {infoAkun(form.category).lama ? 'Kategori lama — pilih akun yang tepat.' : 'Akun ini ditandai perlu dicek. Simpan untuk menandai sudah benar.'}
                  </p>
                )}
              </div>
              <div>
                <label className="text-[11px] uppercase tracking-wider text-mocha font-semibold">Sumber Dana</label>
                <select data-testid="kas-edit-source" value={form.source} onChange={(e) => setForm({ ...form, source: e.target.value })}
                  className="mt-1 w-full rounded-xl border border-input bg-white px-3 py-2.5 text-sm outline-none focus:ring-2 focus:ring-brown/30">
                  {Object.entries(SOURCE_L).map(([v, l]) => <option key={v} value={v}>{l}</option>)}
                </select>
              </div>
            </div>
          )}
          {!isTransfer && (
            <div>
              <label className="text-[11px] uppercase tracking-wider text-mocha font-semibold">Cara Bayar</label>
              <select data-testid="kas-edit-method" value={form.method} onChange={(e) => setForm({ ...form, method: e.target.value })}
                className="mt-1 w-full rounded-xl border border-input bg-white px-3 py-2.5 text-sm outline-none focus:ring-2 focus:ring-brown/30">
                {Object.entries(METHOD_L).map(([v, l]) => <option key={v} value={v}>{l}</option>)}
              </select>
            </div>
          )}
          {isIn && (
            <div className="grid grid-cols-2 gap-2">
              <input data-testid="kas-edit-customer" value={form.customer_name} onChange={(e) => setForm({ ...form, customer_name: e.target.value })} placeholder="Customer"
                className="rounded-xl border border-input bg-white px-3 py-2.5 text-sm outline-none" />
              <input data-testid="kas-edit-order" value={form.order_number} onChange={(e) => setForm({ ...form, order_number: e.target.value })} placeholder="ID Order"
                className="rounded-xl border border-input bg-white px-3 py-2.5 text-sm outline-none" />
            </div>
          )}
          <input data-testid="kas-edit-notes" value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} placeholder="Keterangan"
            className="w-full rounded-xl border border-input bg-white px-3 py-2.5 text-sm outline-none" />
          <button data-testid="kas-edit-save" onClick={submit}
            className="w-full rounded-xl bg-brown text-white py-3 text-sm font-bold hover:bg-brown-med">Simpan Koreksi</button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function NotaDialog({ entry, onClose, onSaved }) {
  const [existing, setExisting] = useState(null); // null = memuat, '' = tidak ada
  const [baru, setBaru] = useState('');
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!entry) { setExisting(null); setBaru(''); return; }
    setBaru('');
    if (!entry.has_photo) { setExisting(''); return; }
    setExisting(null);
    api.get(`/kas/entry/${entry.id}/photo`)
      .then((r) => setExisting(r.data.image_base64 || ''))
      .catch(() => setExisting(''));
  }, [entry]);

  if (!entry) return null;

  const simpan = async () => {
    setBusy(true);
    try {
      await api.post(`/kas/entry/${entry.id}/photo`, { image_base64: baru, mime_type: 'image/jpeg' });
      toast.success('Foto nota tersimpan.');
      onClose(); onSaved();
    } catch (err) { toast.error(apiError(err)); }
    finally { setBusy(false); }
  };

  const hapus = async () => {
    try {
      await api.delete(`/kas/entry/${entry.id}/photo`);
      toast.success('Foto nota dihapus.');
      onClose(); onSaved();
    } catch (err) { toast.error(apiError(err)); }
  };

  return (
    <Dialog open={!!entry} onOpenChange={onClose}>
      <DialogContent data-testid="kas-nota-dialog" className="bg-ivory max-w-md max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="font-display text-brown-dark">Nota — {entry.category}</DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          <p className="text-xs text-mocha">
            {formatDate(entry.date)} · <b className="text-ink">{formatIDR(entry.amount)}</b>
            {entry.notes ? ` · ${entry.notes}` : ''}
          </p>

          {existing === null && <p className="text-xs text-mocha">Memuat foto…</p>}

          {existing && !baru && (
            <div className="rounded-xl border border-border bg-white p-2">
              <img src={`data:image/jpeg;base64,${existing}`} alt="Nota" className="w-full max-h-[50vh] object-contain rounded-lg" data-testid="kas-nota-image" />
              <div className="flex gap-2 mt-2">
                <button onClick={() => setExisting('')} data-testid="kas-nota-replace"
                  className="flex-1 rounded-lg border border-border py-2 text-xs font-bold text-mocha hover:bg-ivory">Foto Ulang</button>
                <button onClick={hapus} data-testid="kas-nota-delete"
                  className="rounded-lg border border-danger/40 text-danger px-3 py-2 text-xs font-bold hover:bg-danger/10">Hapus</button>
              </div>
            </div>
          )}

          {existing === '' && (
            <>
              {entry.no_receipt_reason && !baru && (
                <p className="text-[11px] text-mocha bg-white rounded-xl border border-border p-2.5 leading-snug">
                  Dicatat tanpa nota — alasan: <b className="text-ink">{entry.no_receipt_reason}</b>
                </p>
              )}
              <NotaCapture value={baru} onChange={setBaru} testid="kas-nota" />
              <button onClick={simpan} disabled={!baru || busy} data-testid="kas-nota-save"
                className="w-full rounded-xl bg-brown text-white py-3 text-sm font-bold hover:bg-brown-med disabled:opacity-50">
                {busy ? 'Menyimpan…' : 'Simpan Nota'}
              </button>
            </>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}

function TitipanDialog({ open, onClose, rincian = [], total = 0 }) {
  const [dariOrder, setDariOrder] = useState(null);
  const [busy, setBusy] = useState(false);

  // Dihitung ulang dari daftar order supaya daftarnya tidak pernah kosong
  // walau backend belum sempat diperbarui.
  useEffect(() => {
    if (!open) return;
    let batal = false;
    setBusy(true);
    api.get('/transactions')
      .then((res) => {
        if (batal) return;
        const raw = res.data;
        const list = Array.isArray(raw) ? raw : (raw?.items || raw?.data || []);
        const hari = todayStr();
        const rows = list
          .filter((t) => t.status !== 'cancelled' && (t.use_date || '') > hari && Number(t.paid || 0) > 0)
          .map((t) => ({
            transaction_number: t.transaction_number || '',
            customer_name: t.customer_name || '',
            use_date: t.use_date || '',
            total: Number(t.total || 0),
            paid: Number(t.paid || 0),
            remaining: Number(t.remaining || 0),
          }))
          .sort((a, b) => (a.use_date + a.transaction_number).localeCompare(b.use_date + b.transaction_number));
        setDariOrder(rows);
      })
      .catch(() => { if (!batal) setDariOrder(null); })
      .finally(() => { if (!batal) setBusy(false); });
    return () => { batal = true; };
  }, [open]);

  if (!open) return null;

  const daftar = (dariOrder && dariOrder.length) ? dariOrder : rincian;
  const totalTampil = daftar.length ? daftar.reduce((s, r) => s + r.paid, 0) : total;
  const perBulan = {};
  daftar.forEach((r) => {
    const m = (r.use_date || '').slice(0, 7);
    (perBulan[m] = perBulan[m] || []).push(r);
  });

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent data-testid="kas-titipan-dialog" className="bg-ivory max-w-3xl max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="font-display text-brown-dark">Uang Titipan — milik siapa saja</DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          <p className="text-[11px] text-mocha leading-snug bg-white rounded-xl border border-border p-2.5">
            Semua uang yang sudah kamu terima untuk acara yang <b>belum terjadi</b> — termasuk order yang sudah lunas.
            Kalau ada yang batal, uang inilah yang harus dikembalikan.
          </p>

          <div className="rounded-xl bg-white border border-border p-3 flex items-baseline justify-between">
            <div>
              <p className="text-[10px] uppercase tracking-wider text-mocha font-semibold">Total titipan</p>
              <p className="text-[11px] text-mocha mt-0.5">{busy ? 'memuat daftar…' : `${daftar.length} order`}</p>
            </div>
            <p className="font-display text-2xl text-gold-dark">{formatIDR(totalTampil)}</p>
          </div>

          {Object.entries(perBulan).sort().map(([m, rows]) => (
            <div key={m} className="bg-white rounded-xl border border-border overflow-hidden">
              <div className="flex items-baseline justify-between px-3 py-2 bg-ivory/70 border-b border-border">
                <p className="text-xs font-bold text-brown-dark">{BULAN_ID(m)}</p>
                <p className="text-xs font-bold text-gold-dark">
                  {formatIDR(rows.reduce((s, r) => s + r.paid, 0))} · {rows.length} order
                </p>
              </div>
              <table className="w-full text-[12.5px]">
                <thead>
                  <tr className="text-left text-[9.5px] uppercase tracking-wider text-mocha border-b border-border/70">
                    <th className="px-3 py-2 font-bold">Tgl Pakai</th>
                    <th className="px-3 py-2 font-bold">Order</th>
                    <th className="px-3 py-2 font-bold">Pelanggan</th>
                    <th className="px-3 py-2 font-bold text-right">Total</th>
                    <th className="px-3 py-2 font-bold text-right">Sudah Dibayar</th>
                    <th className="px-3 py-2 font-bold text-right">Sisa</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((r) => (
                    <tr key={r.transaction_number} className="border-b border-border/50 last:border-0"
                      data-testid={`titipan-row-${r.transaction_number}`}>
                      <td className="px-3 py-2 text-mocha whitespace-nowrap">{formatDate(r.use_date)}</td>
                      <td className="px-3 py-2 font-bold text-brown">{r.transaction_number}</td>
                      <td className="px-3 py-2 font-semibold">{r.customer_name || '-'}</td>
                      <td className="px-3 py-2 text-right text-mocha whitespace-nowrap">{formatIDR(r.total)}</td>
                      <td className="px-3 py-2 text-right font-bold text-gold-dark whitespace-nowrap">{formatIDR(r.paid)}</td>
                      <td className={`px-3 py-2 text-right whitespace-nowrap font-semibold ${r.remaining > 0 ? 'text-danger' : 'text-success'}`}>
                        {r.remaining > 0 ? formatIDR(r.remaining) : 'Lunas'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ))}

          {!busy && daftar.length === 0 && (
            <p className="text-sm text-mocha text-center py-6">Tidak ada uang titipan — semua acara sudah lewat.</p>
          )}
          {busy && daftar.length === 0 && (
            <p className="text-sm text-mocha text-center py-6">Memuat daftar order…</p>
          )}
          {Math.abs(totalTampil - total) > 1 && total > 0 && daftar.length > 0 && (
            <p className="text-[11px] text-mocha leading-snug bg-gold/10 border border-gold/40 rounded-xl p-2.5">
              Catatan: kartu di halaman menampilkan {formatIDR(total)} sedangkan daftar ini {formatIDR(totalTampil)}.
              Bedanya biasanya karena halaman belum dimuat ulang — tekan F5.
            </p>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}

function TutupTunaiDialog({ open, onClose, cashSistem, perluTutup, belumTutup = [], onSaved }) {
  const [fisik, setFisik] = useState('');
  const [jenis, setJenis] = useState('utang');
  const [tgl, setTgl] = useState('');
  const [notes, setNotes] = useState('');
  const [busy, setBusy] = useState(false);
  const [saldoTgl, setSaldoTgl] = useState(null);   // saldo tunai per tanggal terpilih
  const [rincianTgl, setRincianTgl] = useState(null); // asal-usul angka itu
  const [sudahTutup, setSudahTutup] = useState(false);
  const [cekBusy, setCekBusy] = useState(false);
  const [riwayat, setRiwayat] = useState([]);

  const muatRiwayat = () => {
    api.get('/kas/tutup-tunai')
      .then((r) => setRiwayat(Array.isArray(r.data) ? r.data : []))
      .catch(() => setRiwayat([]));
  };

  useEffect(() => {
    if (open) { setFisik(''); setJenis('utang'); setTgl(perluTutup ? AKHIR_BULAN(perluTutup) : ''); setNotes(''); setSaldoTgl(null); setRincianTgl(null); setSudahTutup(false); muatRiwayat(); }
  }, [open, perluTutup]);

  // Saldo tunai mengikuti tanggal yang dipilih, bukan saldo hari ini.
  useEffect(() => {
    if (!open || !tgl) { setSaldoTgl(null); setRincianTgl(null); setSudahTutup(false); return; }
    let batal = false;
    setCekBusy(true);
    api.get(`/kas/saldo-tunai?date=${tgl}`)
      .then((res) => { if (!batal) { setSaldoTgl(res.data.cash); setRincianTgl(res.data.rincian || null); setSudahTutup(!!res.data.sudah_ditutup); } })
      .catch(() => { if (!batal) { setSaldoTgl(null); setRincianTgl(null); setSudahTutup(false); } })
      .finally(() => { if (!batal) setCekBusy(false); });
    return () => { batal = true; };
  }, [open, tgl]);

  const dipakai = saldoTgl != null ? saldoTgl : cashSistem;
  const isi = parseFloat(fisik) || 0;
  const selisih = fisik === '' ? null : dipakai - isi;

  const batalkan = async (row) => {
    setBusy(true);
    try {
      await api.delete(`/kas/tutup-tunai/${row.id}`);
      toast.success(`Penutupan ${formatDate(row.date)} dibatalkan. Saldo tunai kembali seperti sebelumnya.`);
      muatRiwayat();
      setTgl((t) => t);
      const res = await api.get(`/kas/saldo-tunai?date=${tgl || row.date}`);
      setSaldoTgl(res.data.cash); setRincianTgl(res.data.rincian || null); setSudahTutup(!!res.data.sudah_ditutup);
      onSaved();
    } catch (err) { toast.error(apiError(err)); }
    finally { setBusy(false); }
  };

  const submit = async () => {
    setBusy(true);
    try {
      const r = await api.post('/kas/tutup-tunai', {
        tunai_fisik: isi, jenis, date: tgl || undefined, notes,
      });
      const s = r.data.selisih;
      toast.success(
        Math.abs(s) < 1 ? 'Cocok. Kas tunai bersih, tidak ada yang terpakai.'
          : s > 0 ? `Rp${Math.round(s).toLocaleString('id-ID')} dicatat sebagai ${jenis === 'prive' ? 'Prive' : 'Utang Pemilik'}. Cash sekarang ${formatIDR(isi)}.`
          : `Koreksi tambah Rp${Math.round(Math.abs(s)).toLocaleString('id-ID')}. Cash sekarang ${formatIDR(isi)}.`
      );
      onClose(); onSaved();
    } catch (err) { toast.error(apiError(err)); }
    finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent data-testid="kas-tutup-dialog" className="bg-ivory max-w-md max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="font-display text-brown-dark">Tutup Kas Tunai</DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          <p className="text-[11px] text-mocha leading-snug bg-white rounded-xl border border-border p-2.5">
            Uang tunai adalah tempat singgah, bukan tempat menyimpan. Lakukan ini <b>setelah semua setoran bulan ini selesai</b>.
            Sisa yang tidak ada wujudnya otomatis dicatat sebagai Prive, dan Cash kembali sesuai kenyataan.
          </p>

          {belumTutup.length > 1 && (
            <div className="rounded-xl bg-white border border-border p-3">
              <p className="text-[10px] uppercase tracking-wider text-mocha font-semibold mb-2">Bulan yang belum ditutup — kerjakan dari yang paling lama</p>
              <div className="flex flex-wrap gap-1.5">
                {belumTutup.map((m) => (
                  <button key={m} type="button" onClick={() => setTgl(AKHIR_BULAN(m))} data-testid={`kas-tutup-pilih-${m}`}
                    className={`rounded-lg border px-2.5 py-1.5 text-[11px] font-bold ${tgl === AKHIR_BULAN(m) ? 'border-brown bg-brown/10 text-brown-dark' : 'border-border bg-white text-mocha hover:bg-ivory'}`}>
                    {BULAN_ID(m)}
                  </button>
                ))}
              </div>
            </div>
          )}

          <div className="rounded-xl bg-white border border-border p-3">
            <p className="text-[10px] uppercase tracking-wider text-mocha font-semibold">
              {tgl ? `Cash menurut sistem per ${formatDate(tgl)}` : 'Cash menurut sistem hari ini'}
            </p>
            <p className="font-display text-2xl text-brown-dark mt-1 tracking-tight" data-testid="kas-tutup-sistem">
              {cekBusy ? '…' : formatIDR(dipakai)}
            </p>
            <p className="text-[11px] text-mocha mt-1">
              {tgl
                ? 'Saldo tunai sampai tanggal itu saja — uang tunai bulan sesudahnya tidak ikut dihitung.'
                : 'Segini yang seharusnya masih ada di lacimu. Isi tanggal kalau mau menutup bulan yang sudah lewat.'}
            </p>

            {rincianTgl && (
              <div className="mt-2.5 pt-2.5 border-t border-border space-y-1" data-testid="kas-tutup-rincian">
                <p className="text-[10px] uppercase tracking-wider text-mocha font-semibold">Asal angka ini</p>
                {rincianTgl.saldo_awal > 0 && (
                  <div className="flex justify-between text-[11.5px]"><span className="text-mocha">Saldo awal tunai</span><span>{formatIDR(rincianTgl.saldo_awal)}</span></div>
                )}
                <div className="flex justify-between text-[11.5px]"><span className="text-mocha">Tunai diterima s/d tanggal ini</span><span className="text-success">+ {formatIDR(rincianTgl.masuk)}</span></div>
                {rincianTgl.terima_transfer > 0 && (
                  <div className="flex justify-between text-[11.5px]"><span className="text-mocha">Tarikan ke tunai</span><span className="text-success">+ {formatIDR(rincianTgl.terima_transfer)}</span></div>
                )}
                <div className="flex justify-between text-[11.5px]"><span className="text-mocha">Sudah disetor ke rekening</span><span className="text-danger">− {formatIDR(rincianTgl.setor)}</span></div>
                <div className="flex justify-between text-[11.5px]"><span className="text-mocha">Pengeluaran dibayar tunai</span><span className="text-danger">− {formatIDR(rincianTgl.keluar)}</span></div>
                <div className="flex justify-between text-[12px] font-bold pt-1.5 border-t border-border/70">
                  <span>Sisa tunai seharusnya</span><span className="text-brown-dark">{formatIDR(rincianTgl.saldo)}</span>
                </div>
              </div>
            )}
          </div>

          {sudahTutup && (
            <div className="rounded-xl bg-danger/10 border border-danger/30 p-3" data-testid="kas-tutup-sudah">
              <p className="text-xs text-danger font-semibold">Tanggal ini sudah pernah ditutup</p>
              <p className="text-[11px] text-mocha mt-0.5 leading-snug">
                Menutup dua kali akan mencatat pengambilan dua kali. Batalkan dulu penutupan lamanya di daftar bawah, baru tutup ulang.
              </p>
            </div>
          )}

          {riwayat.length > 0 && (
            <div className="rounded-xl bg-white border border-border p-3" data-testid="kas-tutup-riwayat">
              <p className="text-[10px] uppercase tracking-wider text-mocha font-semibold mb-2">
                Penutupan yang sudah pernah dilakukan
              </p>
              <div className="space-y-1.5">
                {riwayat.map((h) => {
                  const sel = Number(h.selisih) || 0;
                  const label = Math.abs(sel) < 1
                    ? 'cocok, tidak ada selisih'
                    : sel > 0
                      ? `${h.jenis === 'prive' ? 'Prive' : 'Utang Pemilik'} ${formatIDR(sel)}`
                      : `Koreksi tambah ${formatIDR(Math.abs(sel))}`;
                  return (
                    <div key={h.id} className="flex items-center justify-between gap-2 rounded-lg bg-ivory/70 border border-border px-2.5 py-2">
                      <div className="min-w-0">
                        <p className="text-[11.5px] font-bold text-ink">{formatDate(h.date)}</p>
                        <p className="text-[10.5px] text-mocha leading-snug">
                          Fisik {formatIDR(h.tunai_fisik || 0)} vs sistem {formatIDR(h.cash_sistem || 0)} — {label}
                        </p>
                      </div>
                      <button type="button" disabled={busy} onClick={() => batalkan(h)}
                        data-testid={`kas-tutup-batal-${h.id}`}
                        className="shrink-0 rounded-lg border border-danger/40 text-danger px-2.5 py-1.5 text-[10.5px] font-bold hover:bg-danger/10 disabled:opacity-60">
                        Batalkan
                      </button>
                    </div>
                  );
                })}
              </div>
              <p className="text-[10px] text-mocha leading-snug mt-2">
                Membatalkan akan menghapus baris kas yang dibuat penutupan itu, dan saldo tunai kembali seperti sebelum ditutup.
              </p>
            </div>
          )}

          <div className="grid grid-cols-2 gap-2">
            <div>
              <label className="text-[11px] uppercase tracking-wider text-mocha font-semibold">Tanggal Tutup</label>
              <input data-testid="kas-tutup-date" type="date" value={tgl} onChange={(e) => setTgl(e.target.value)}
                className="mt-1 w-full rounded-xl border border-input bg-white px-3 py-2.5 text-sm outline-none focus:ring-2 focus:ring-brown/30" />
            </div>
            <div>
              <label className="text-[11px] uppercase tracking-wider text-mocha font-semibold">Tunai yang Benar-benar Ada</label>
              <MoneyInput data-testid="kas-tutup-fisik" value={fisik} onChange={setFisik} placeholder="0"
                className="mt-1 w-full rounded-xl border border-input bg-white px-3 py-2.5 text-sm outline-none focus:ring-2 focus:ring-brown/30" />
            </div>
          </div>
          <p className="text-[10px] text-mocha leading-snug -mt-1">
            Hitung fisik. Kalau semua sudah disetor dan laci kosong, isi 0.
          </p>

          {selisih !== null && selisih > 0 && (
            <div className="rounded-xl bg-white border border-border p-3" data-testid="kas-tutup-jenis">
              <p className="text-[11px] uppercase tracking-wider text-mocha font-semibold mb-2">Sisa ini dicatat sebagai</p>
              <div className="grid grid-cols-2 gap-2">
                {[
                  { v: 'utang', t: 'Utang Pemilik', d: 'Akan dikembalikan ke bisnis' },
                  { v: 'prive', t: 'Prive', d: 'Diambil sebagai hak, selesai' },
                ].map((o) => (
                  <button key={o.v} type="button" onClick={() => setJenis(o.v)} data-testid={`kas-tutup-jenis-${o.v}`}
                    className={`text-left rounded-xl border p-2.5 transition ${jenis === o.v ? 'border-brown bg-brown/8 ring-2 ring-brown/20' : 'border-border bg-white hover:bg-ivory'}`}>
                    <p className={`text-xs font-bold ${jenis === o.v ? 'text-brown-dark' : 'text-ink'}`}>{o.t}</p>
                    <p className="text-[10px] text-mocha leading-snug mt-0.5">{o.d}</p>
                  </button>
                ))}
              </div>
            </div>
          )}

          {selisih !== null && (
            <div className={`rounded-xl p-3 border ${Math.abs(selisih) < 1 ? 'bg-success/10 border-success/30' : selisih > 0 ? 'bg-gold/10 border-gold/40' : 'bg-danger/10 border-danger/30'}`}
              data-testid="kas-tutup-hasil">
              <p className="text-[10px] uppercase tracking-wider font-semibold text-mocha">
                {Math.abs(selisih) < 1 ? 'Hasil' : selisih > 0 ? `Akan dicatat sebagai ${jenis === 'prive' ? 'Prive' : 'Utang Pemilik'}` : 'Akan dicatat sebagai Koreksi Kas'}
              </p>
              <p className={`font-display text-2xl mt-0.5 ${Math.abs(selisih) < 1 ? 'text-success' : selisih > 0 ? 'text-gold-dark' : 'text-danger'}`}>
                {formatIDR(Math.abs(selisih))}
              </p>
              <p className="text-[11px] text-mocha mt-1 leading-snug">
                {Math.abs(selisih) < 1
                  ? 'Cocok. Semua uang tunai sudah tersalur — tidak ada yang terpakai di luar catatan.'
                  : selisih > 0
                    ? `Uang tunai kurang segini dari catatan. Karena biaya bisnis sudah kamu catat, sisa ini terpakai untuk keperluan pribadi. Setelah disimpan, saldo tunai per ${tgl ? formatDate(tgl) : 'hari ini'} menjadi ${formatIDR(isi)}${jenis === 'utang' ? ' dan sisa utang pemilik bertambah' : ''}.`
                    : `Uang tunai lebih banyak dari catatan — ada pemasukan yang belum dicatat. Setelah disimpan, Cash menjadi ${formatIDR(isi)}.`}
              </p>
            </div>
          )}

          <div>
            <label className="text-[11px] uppercase tracking-wider text-mocha font-semibold">Catatan (opsional)</label>
            <input data-testid="kas-tutup-notes" value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="mis. tutup kas September"
              className="mt-1 w-full rounded-xl border border-input bg-white px-3 py-2.5 text-sm outline-none focus:ring-2 focus:ring-brown/30" />
          </div>

          <button data-testid="kas-tutup-save" onClick={submit} disabled={fisik === '' || busy || cekBusy}
            className="w-full rounded-xl bg-brown-dark text-white py-3 text-sm font-bold hover:bg-brown disabled:opacity-50">
            {busy ? 'Menyimpan…' : 'Simpan Tutup Kas Tunai'}
          </button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function RekonDialog({ open, onClose, ringkasan, onSaved }) {
  const [saldo, setSaldo] = useState('');
  const [utama, setUtama] = useState('');
  const [laci, setLaci] = useState('');
  const [tgl, setTgl] = useState('');
  const [notes, setNotes] = useState('');
  useEffect(() => { if (open) { setSaldo(''); setUtama(''); setLaci(''); setTgl(''); setNotes(''); } }, [open]);

  const tempat = ringkasan?.per_tempat || {};
  const harusnya = tempat.bank_ops || 0;
  const utamaHarusnya = tempat.bank_utama || 0;
  const tunaiHarusnya = tempat.cash || 0;
  const isi = parseFloat(saldo) || 0;
  const isiUtama = parseFloat(utama) || 0;
  const isiLaci = parseFloat(laci) || 0;
  const selisih = saldo === '' ? null : isi - harusnya;
  const selisihUtama = utama === '' ? null : isiUtama - utamaHarusnya;
  const selisihTunai = laci === '' ? null : isiLaci - tunaiHarusnya;

  const submit = async () => {
    try {
      const r = await api.post('/kas/rekonsiliasi', {
        saldo_rekening: isi,
        saldo_utama: utama === '' ? undefined : isiUtama,
        tunai_laci: laci === '' ? undefined : isiLaci,
        date: tgl || undefined, notes });
      toast.success(r.data.selisih === 0 ? 'Cocok. Pembukuanmu bersih.' : `Tercatat. Selisih ${formatIDR(r.data.selisih)}.`);
      onClose(); onSaved();
    } catch (err) { toast.error(apiError(err)); }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent data-testid="kas-rekon-dialog" className="bg-ivory max-w-md">
        <DialogHeader>
          <DialogTitle className="font-display text-brown-dark">Cocokkan dengan Rekening</DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          <div className="rounded-xl bg-white border border-border p-3 space-y-1.5">
            <p className="text-[10px] uppercase tracking-wider text-mocha font-semibold">Menurut sistem seharusnya ada</p>
            {TEMPAT.map((t) => (
              <div key={t.key} className="flex items-center justify-between text-sm">
                <span className="text-ink flex items-center gap-2"><span className={`w-2 h-2 rounded-full ${SOURCE_DOT[t.key]}`} />{SOURCE_L[t.key]}</span>
                <span className="font-display text-base text-brown-dark">{formatIDR(tempat[t.key] || 0)}</span>
              </div>
            ))}
          </div>
          <div className="grid grid-cols-2 gap-2">
            <div>
              <label className="text-[11px] uppercase tracking-wider text-mocha font-semibold">Tanggal Cek</label>
              <input data-testid="kas-rekon-date" type="date" value={tgl} onChange={(e) => setTgl(e.target.value)}
                className="mt-1 w-full rounded-xl border border-input bg-white px-3 py-2.5 text-sm outline-none focus:ring-2 focus:ring-brown/30" />
            </div>
            <div>
              <label className="text-[11px] uppercase tracking-wider text-mocha font-semibold">Saldo Rek. Operasional Asli</label>
              <MoneyInput data-testid="kas-rekon-saldo" value={saldo} onChange={setSaldo} placeholder="0"
                className="mt-1 w-full rounded-xl border border-input bg-white px-3 py-2.5 text-sm outline-none focus:ring-2 focus:ring-brown/30" />
            </div>
          </div>

          <div>
            <label className="text-[11px] uppercase tracking-wider text-mocha font-semibold">Saldo Rek. Bisnis Utama Asli</label>
            <MoneyInput data-testid="kas-rekon-utama" value={utama} onChange={setUtama} placeholder="kosongkan kalau tidak dicek"
              className="mt-1 w-full rounded-xl border border-input bg-white px-3 py-2.5 text-sm outline-none focus:ring-2 focus:ring-brown/30" />
          </div>

          {selisihUtama !== null && (
            <div className={`rounded-xl p-3 border ${selisihUtama === 0 ? 'bg-success/10 border-success/30' : 'bg-gold/10 border-gold/40'}`} data-testid="kas-rekon-selisih-utama">
              <p className="text-[10px] uppercase tracking-wider font-semibold text-mocha">Selisih Rek. Bisnis Utama</p>
              <p className={`font-display text-xl mt-0.5 ${selisihUtama === 0 ? 'text-success' : 'text-gold-dark'}`}>{formatIDR(selisihUtama)}</p>
            </div>
          )}

          <div>
            <label className="text-[11px] uppercase tracking-wider text-mocha font-semibold">Uang Tunai yang Benar-benar Ada (hitung fisik)</label>
            <MoneyInput data-testid="kas-rekon-laci" value={laci} onChange={setLaci} placeholder="kosongkan kalau tidak dihitung"
              className="mt-1 w-full rounded-xl border border-input bg-white px-3 py-2.5 text-sm outline-none focus:ring-2 focus:ring-brown/30" />
            <p className="text-[11px] text-mocha mt-1 leading-snug">
              Hitung semua uang tunai bisnis yang belum disetor — di laci, dompet, di mana pun.
            </p>
          </div>

          {selisihTunai !== null && (
            <div className={`rounded-xl p-3 border ${selisihTunai === 0 ? 'bg-success/10 border-success/30' : 'bg-gold/10 border-gold/40'}`} data-testid="kas-rekon-selisih-tunai">
              <p className="text-[10px] uppercase tracking-wider font-semibold text-mocha">Selisih Tunai</p>
              <p className={`font-display text-xl mt-0.5 ${selisihTunai === 0 ? 'text-success' : 'text-gold-dark'}`}>{formatIDR(selisihTunai)}</p>
              <p className="text-[11px] text-mocha mt-1 leading-snug">
                {selisihTunai === 0 ? 'Tunai cocok. Semua uang tunai masih utuh.'
                  : selisihTunai < 0 ? `Uang tunai kurang ${formatIDR(Math.abs(selisihTunai))} dari catatan. Sebesar itulah kas bisnis yang sudah terpakai tanpa dicatat — sebagian besar biasanya keperluan pribadi. Catat sebagai pengeluaran "Prive / Pemakaian Pribadi" supaya kas kembali jujur.`
                  : 'Tunai lebih banyak dari catatan. Ada uang masuk yang belum dicatat.'}
              </p>
            </div>
          )}
          {selisih !== null && (
            <div className={`rounded-xl p-3 border ${selisih === 0 ? 'bg-success/10 border-success/30' : 'bg-danger/10 border-danger/30'}`}>
              <p className="text-[10px] uppercase tracking-wider font-semibold text-mocha">Selisih</p>
              <p className={`font-display text-xl mt-0.5 ${selisih === 0 ? 'text-success' : 'text-danger'}`}>{formatIDR(selisih)}</p>
              <p className="text-[11px] text-mocha mt-1 leading-snug">
                {selisih === 0 ? 'Cocok. Semua uang masuk dan keluar sudah tercatat.'
                  : selisih < 0 ? 'Rekening lebih kecil dari catatan. Ada pengeluaran yang belum dicatat, atau uang bisnis terpakai di luar operasional.'
                  : 'Rekening lebih besar dari catatan. Ada pemasukan yang belum dicatat.'}
              </p>
            </div>
          )}
          <div>
            <label className="text-[11px] uppercase tracking-wider text-mocha font-semibold">Catatan</label>
            <input data-testid="kas-rekon-notes" value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="mis. cek rutin akhir bulan"
              className="mt-1 w-full rounded-xl border border-input bg-white px-3 py-2.5 text-sm outline-none focus:ring-2 focus:ring-brown/30" />
          </div>
          <button data-testid="kas-rekon-save" onClick={submit} disabled={saldo === ''}
            className="w-full rounded-xl bg-brown text-white py-3 text-sm font-bold hover:bg-brown-med disabled:opacity-50">
            Simpan Hasil Pencocokan
          </button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

// Kartu keadaan jembatan Telegram.
//
// Sengaja menampilkan LANGKAH YANG KURANG, bukan sekadar "aktif / tidak aktif".
// Tiga hal harus beres dan ketiganya di tempat berbeda: kunci bot di berkas .env
// server, alamat webhook didaftarkan ke Telegram, dan grupnya dikunci lewat
// perintah /pasang. Tanpa kartu ini, yang kurang satu pun kelihatannya sama saja:
// foto dikirim ke grup lalu tidak terjadi apa-apa.
//
// Kalau pengguna bukan owner, /telegram/status menjawab 403 dan kartunya
// disembunyikan tanpa pesan kesalahan — karyawan tidak perlu melihat ini.
function KartuDendaBelum() {
  /* Denda yang sudah disepakati tapi uangnya belum diterima.

     Sengaja dipisah dari saldo kas: selama ada di sini, nilainya BUKAN uang.
     Baru saat ditekan salah satu tombol sumber dana, dia masuk buku kas sebagai
     pemasukan kategori Denda. Kartunya hilang sendiri kalau tidak ada yang
     menunggak, supaya halaman Kas tidak penuh kartu kosong. */
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState('');

  const muat = useCallback(() => {
    api.get('/kas/denda-belum').then((r) => setData(r.data)).catch(() => setData(null));
  }, []);
  useEffect(() => { muat(); }, [muat]);

  if (!data || !data.jumlah) return null;

  const lunasi = async (row, sumber) => {
    setBusy(row.transaction_id + sumber);
    try {
      await api.post(`/transactions/${row.transaction_id}/denda/bayar`, { sumber });
      toast.success(`Denda ${formatIDR(row.nominal)} masuk kas`);
      muat();
    } catch (err) { toast.error(apiError(err)); }
    finally { setBusy(''); }
  };

  return (
    <div className="bg-white rounded-2xl border border-danger/30 shadow-soft p-4" data-testid="kas-denda-belum">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <p className="text-[10px] uppercase tracking-wider text-mocha font-semibold flex items-center gap-1.5">
            <Scale size={11} /> Denda belum dibayar
          </p>
          <p className="text-sm text-ink font-bold mt-0.5" data-testid="kas-denda-belum-ringkas">
            {data.jumlah} denda · {formatIDR(data.nilai)}
          </p>
          <p className="text-[11px] text-mocha mt-0.5">
            Belum masuk saldo kas — uangnya memang belum diterima.
          </p>
        </div>
      </div>

      <div className="mt-3 space-y-2">
        {data.rows.map((row) => (
          <div key={row.transaction_id}
               className="rounded-xl bg-ivory/60 border border-brown/10 p-2.5"
               data-testid={`kas-denda-row-${row.order_number}`}>
            <div className="flex flex-wrap items-baseline justify-between gap-2">
              <span className="text-xs">
                <b>{row.order_number}</b> · {row.customer_name}
                <span className="block text-[11px] text-mocha">
                  {(row.jenis || []).join(' + ') || 'Denda'}
                  {row.telat_hari > 0 && <> · telat {row.telat_hari} hari</>}
                  {row.tanggal && <> · {row.tanggal}</>}
                </span>
              </span>
              <span className="text-sm font-bold text-danger">{formatIDR(row.nominal)}</span>
            </div>
            {row.catatan && <p className="text-[11px] text-mocha mt-1">{row.catatan}</p>}
            <div className="flex flex-wrap gap-1.5 mt-2">
              <span className="text-[11px] text-mocha self-center">Sudah dibayar lewat:</span>
              {[['cash', 'Tunai'], ['bank_ops', 'Rek. Ops'], ['bank_utama', 'Rek. Utama']].map(([v, t]) => (
                <button
                  key={v}
                  type="button"
                  disabled={!!busy}
                  data-testid={`kas-denda-bayar-${row.order_number}-${v}`}
                  onClick={() => lunasi(row, v)}
                  className="rounded-full border border-brown/15 bg-white px-3 py-1 text-[11px] font-semibold text-brown-dark hover:bg-brown hover:text-white disabled:opacity-40"
                >
                  {t}
                </button>
              ))}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}


function KartuTelegram() {
  const [st, setSt] = useState(null);
  const [busy, setBusy] = useState(false);

  const muat = useCallback(() => {
    api.get('/telegram/status').then((r) => setSt(r.data)).catch(() => setSt(null));
  }, []);
  useEffect(() => { muat(); }, [muat]);

  if (!st) return null;

  const alamat = st.webhook?.url || '';
  const siap = st.token_ada && st.rahasia_ada && alamat && st.chat_id;

  const kerjakan = async (fn, pesan) => {
    setBusy(true);
    try {
      await fn();
      toast.success(pesan);
      muat();
    } catch (err) { toast.error(apiError(err)); }
    finally { setBusy(false); }
  };

  return (
    <div className="bg-white rounded-2xl border border-border shadow-soft p-4" data-testid="kas-telegram">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0">
          <p className="text-[10px] uppercase tracking-wider text-mocha font-semibold flex items-center gap-1.5">
            <Send size={11} /> Nota lewat Telegram
          </p>
          <p className="text-sm text-ink font-bold mt-0.5" data-testid="kas-telegram-status">
            {siap ? 'Aktif — foto nota di grup langsung tercatat' : 'Belum aktif'}
          </p>
          {siap && (
            <p className="text-[11px] text-mocha leading-snug mt-1">
              Bot <b>@{st.bot?.username || '—'}</b> · grup terkunci
              {st.menunggu > 0 && <> · <b className="text-danger">{st.menunggu} nota menunggu sumber dana</b></>}
            </p>
          )}
          {!siap && (
            <ol className="text-[11px] text-mocha leading-relaxed mt-1.5 space-y-0.5">
              <li className={st.token_ada ? 'text-success' : ''}>
                {st.token_ada ? '✓' : '1.'} Kunci bot di berkas <b>.env</b> server
                {!st.token_ada && <span className="text-danger"> — belum ada</span>}
              </li>
              <li className={st.rahasia_ada ? 'text-success' : ''}>
                {st.rahasia_ada ? '✓' : '2.'} Kunci rahasia webhook di berkas <b>.env</b> server
                {!st.rahasia_ada && <span className="text-danger"> — belum ada</span>}
              </li>
              <li className={alamat ? 'text-success' : ''}>
                {alamat ? '✓' : '3.'} Alamat aplikasi didaftarkan ke Telegram
                {!alamat && <span className="text-danger"> — tekan Sambungkan</span>}
              </li>
              <li className={st.chat_id ? 'text-success' : ''}>
                {st.chat_id ? '✓' : '4.'} Kirim <b>/pasang</b> di grup Telegram-nya
                {!st.chat_id && <span className="text-danger"> — belum dikunci</span>}
              </li>
            </ol>
          )}
          {st.pesan && <p className="text-[11px] text-danger leading-snug mt-1.5">{st.pesan}</p>}
        </div>

        <div className="flex flex-col gap-1.5 shrink-0">
          {st.token_ada && st.rahasia_ada && !alamat && (
            <button data-testid="kas-telegram-sambung" disabled={busy}
              onClick={() => kerjakan(
                () => api.post('/telegram/hubungkan', { base_url: window.location.origin }),
                'Tersambung. Sekarang kirim /pasang di grup Telegram-nya.')}
              className="rounded-xl bg-brown text-white px-4 py-2.5 text-xs font-bold hover:bg-brown-dark disabled:opacity-50">
              Sambungkan
            </button>
          )}
          {alamat && (
            <button data-testid="kas-telegram-lepas" disabled={busy}
              onClick={() => kerjakan(() => api.post('/telegram/lepaskan'),
                'Jembatan dilepas. Foto di grup tidak lagi masuk aplikasi.')}
              className="rounded-xl border border-border bg-white px-4 py-2 text-[11px] font-semibold text-mocha hover:bg-ivory disabled:opacity-50">
              Lepaskan
            </button>
          )}
          {st.chat_id && (
            <button data-testid="kas-telegram-ganti-grup" disabled={busy}
              onClick={() => kerjakan(() => api.delete('/telegram/grup'),
                'Grup dilepas. Kirim /pasang di grup yang baru.')}
              className="rounded-xl border border-border bg-white px-4 py-2 text-[11px] font-semibold text-mocha hover:bg-ivory disabled:opacity-50">
              Ganti grup
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

export default function KasMonitoring() {
  const [data, setData] = useState(null);
  const [dialog, setDialog] = useState(null); // 'in' | 'out' | 'transfer' | 'scan-nota' | 'scan-transfer'

  // Pintasan layar utama: /scan -> /kas?scan=nota -> dialog scan terbuka
  // sendiri, jadi dari ikon cukup satu ketukan lagi untuk sampai ke kamera.
  // Alamatnya dibersihkan setelah dipakai supaya menu Kas biasa tidak ikut
  // membuka dialog saat halaman dimuat ulang.
  useEffect(() => {
    const minta = new URLSearchParams(window.location.search).get('scan');
    if (minta !== 'nota' && minta !== 'transfer') return;
    setDialog(minta === 'nota' ? 'scan-nota' : 'scan-transfer');
    window.history.replaceState({}, '', window.location.pathname);
  }, []);
  const [voidFor, setVoidFor] = useState(null);
  const [filters, setFilters] = useState({ start: '', end: '', direction: '', source: '', q: '', perlu_cek: '' });
  const [saldoOpen, setSaldoOpen] = useState(false);
  const [saldoForm, setSaldoForm] = useState({ cash: '', bank_ops: '', bank_utama: '' });
  const [rekonOpen, setRekonOpen] = useState(false);
  const [tutupOpen, setTutupOpen] = useState(false);
  const [titipanOpen, setTitipanOpen] = useState(false);
  const [tampilVoid, setTampilVoid] = useState(false);
  const [buildBackend, setBuildBackend] = useState(null);
  const [hapusVoidOpen, setHapusVoidOpen] = useState(false);
  const [hapusBusy, setHapusBusy] = useState(false);
  const [editFor, setEditFor] = useState(null);
  const [notaFor, setNotaFor] = useState(null);
  const [exporting, setExporting] = useState(false);

  const load = useCallback(() => {
    const params = new URLSearchParams();
    Object.entries(filters).forEach(([k, v]) => v && params.set(k, v));
    api.get(`/kas?${params}`).then((r) => setData(r.data)).catch((err) => toast.error(apiError(err)));
  }, [filters]);
  useEffect(() => {
    const t = setTimeout(load, 250);
    return () => clearTimeout(t);
  }, [load]);

  // Cek apakah backend sudah memuat kode yang sama dengan tampilan ini.
  useEffect(() => {
    api.get('/version')
      .then((r) => setBuildBackend(r.data?.build || 'lama'))
      .catch(() => setBuildBackend('lama'));
  }, []);

  const hapusSemuaVoid = async () => {
    setHapusBusy(true);
    try {
      const r = await api.delete('/kas/void');
      toast.success(r.data.dihapus === 0
        ? 'Tidak ada baris void.'
        : `${r.data.dihapus} baris void dihapus. Tabel jadi bersih.`);
      setHapusVoidOpen(false);
      load();
    } catch (err) { toast.error(apiError(err)); }
    finally { setHapusBusy(false); }
  };

  const doVoid = async () => {
    try {
      await api.post(`/kas/entry/${voidFor.id}/void`);
      toast.success('Transaksi di-void (riwayat tetap tersimpan).');
      setVoidFor(null);
      load();
    } catch (err) { toast.error(apiError(err)); }
  };

  const doExport = async (direction) => {
    setExporting(true);
    const tid = toast.loading('Sedang membuat laporan kas...');
    try {
      const params = new URLSearchParams();
      if (filters.start) params.set('start', filters.start);
      if (filters.end) params.set('end', filters.end);
      if (direction) params.set('direction', direction);
      else if (filters.direction) params.set('direction', filters.direction);
      if (filters.source) params.set('source', filters.source);
      const r = await api.get(`/kas/export?${params}`, { responseType: 'blob' });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement('a');
      a.href = url; a.download = `Monitoring_Kas_${new Date().toISOString().slice(0, 10)}.xlsx`; a.click();
      URL.revokeObjectURL(url);
      toast.success('Laporan kas berhasil dibuat.', { id: tid });
    } catch (err) {
      toast.error(apiError(err, 'Tidak ada transaksi kas yang sesuai dengan filter.'), { id: tid });
    } finally { setExporting(false); }
  };

  const saveSaldoAwal = async () => {
    try {
      await api.put('/kas/saldo-awal', { cash: parseFloat(saldoForm.cash) || 0, bank_ops: parseFloat(saldoForm.bank_ops) || 0, bank_utama: parseFloat(saldoForm.bank_utama) || 0 });
      toast.success('Saldo awal disimpan.');
      setSaldoOpen(false);
      load();
    } catch (err) { toast.error(apiError(err)); }
  };

  // Menutup bulan yang sudah lama lewat butuh hitungan uang fisik yang tidak bisa
  // direka ulang. Pemilik boleh menyatakan "cocokkan dari bulan berjalan saja" —
  // datanya tetap utuh, hanya pengingatnya yang berhenti.
  const lewatiBulanLama = async () => {
    const mulai = data?.ringkasan?.bulan_ini;
    if (!mulai) return;
    try {
      await api.put('/kas/tutup-mulai', { mulai });
      toast.success(`Bulan sebelum ${BULAN_ID(mulai)} tidak ditagih lagi.`);
      load();
    } catch (err) { toast.error(apiError(err)); }
  };

  const tagihSemuaBulan = async () => {
    try {
      await api.put('/kas/tutup-mulai', { mulai: '' });
      toast.success('Semua bulan ditagih lagi.');
      load();
    } catch (err) { toast.error(apiError(err)); }
  };

  if (!data) return <p className="text-sm text-mocha py-16 text-center">Memuat monitoring kas…</p>;
  const b = data.balances;
  const r = data.ringkasan || { kas_bisnis: b.total, saldo_awal: 0, total_masuk: 0, total_keluar: 0, belum_disetor: b.cash, seharusnya_di_rekening: b.bank_ops, titipan: 0, titipan_per_bulan: {}, titipan_rincian: [], uang_bebas: b.total, piutang: 0, prive: 0, prive_per_bulan: {}, bayar_per_bulan: {}, utang_ambil: 0, utang_bayar: 0, utang_sisa: 0, pribadi_total: 0, pinjaman_keluar: 0, pinjaman_kembali: 0, pinjaman_sisa: 0, pinjaman_rincian: [], perlu_tutup: null, belum_tutup: [], tutup_mulai: '', tutup_dilewati: [], bulan_ini: '', per_tempat: { cash: b.cash, bank_ops: b.bank_ops, bank_utama: b.bank_utama }, biaya_operasional: 0, tanpa_nota_jumlah: 0, tanpa_nota_nilai: 0 };


  const barisVoid = data.rows.filter((x) => x.status === 'Void');
  const barisTampil = tampilVoid ? data.rows : data.rows.filter((x) => x.status !== 'Void');

  // --- pencarian: kata-kata yang disorot, ringkasan hasil, dan penjaga kolom Saldo ---
  const kataCari = (filters.q || '').toLowerCase().split(/\s+/).filter(Boolean);
  const adaFilter = Object.values(filters).some(Boolean);
  const ringkasCari = (() => {
    if (kataCari.length === 0) return null;
    let masuk = 0; let keluar = 0; let tidakDihitung = 0;
    barisTampil.forEach((r) => {
      if (r.status === 'Void' || r.status === 'Draf') { tidakDihitung += 1; return; }
      if (r.direction === 'in') masuk += Number(r.amount) || 0;
      else if (r.direction === 'out') keluar += Number(r.amount) || 0;
    });
    return { jumlah: barisTampil.length, masuk, keluar, tidakDihitung };
  })();

  const DIR_L = { in: 'Masuk', out: 'Keluar', transfer: 'Transfer' };
  const DIR_CLS = { in: 'bg-success/10 text-success', out: 'bg-danger/10 text-danger', transfer: 'bg-gold/20 text-gold-dark' };

  return (
    <div className="space-y-5" data-testid="kas-page">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="font-display text-3xl text-ink tracking-tight">Monitoring Kas</h1>
          <p className="text-sm text-mocha mt-1">Saldo dihitung otomatis dari seluruh transaksi. Pembayaran customer langsung masuk kas sesuai metode.</p>
        </div>
        <button data-testid="kas-saldo-awal-button" onClick={() => { setSaldoForm({ cash: data.saldo_awal.cash, bank_ops: data.saldo_awal.bank_ops, bank_utama: data.saldo_awal.bank_utama }); setSaldoOpen(true); }}
          className="inline-flex items-center gap-2 rounded-xl bg-white border border-border text-brown px-4 py-2.5 text-xs font-semibold hover:bg-ivory">
          <Pencil size={13} /> Saldo Awal
        </button>
      </div>

      {buildBackend && buildBackend !== BUILD_HARAPAN && (
        <div className="bg-danger/10 rounded-2xl border border-danger/50 shadow-soft p-4" data-testid="kas-versi-beda">
          <p className="text-[10px] uppercase tracking-wider text-danger font-semibold">Backend belum diperbarui</p>
          <p className="text-sm text-ink font-bold mt-0.5">Tampilan ini lebih baru daripada mesin aplikasinya</p>
          <p className="text-[11px] text-mocha leading-snug mt-1">
            Sebagian tombol akan menjawab “Not Found” karena mesinnya belum mengenal perintah baru.
            Tutup aplikasi lewat <b>KLIK-6-BERHENTI</b>, lalu jalankan <b>KLIK-2-JALANKAN</b> lagi.
            <span className="text-mocha/70"> (mesin: {buildBackend} · tampilan: {BUILD_HARAPAN})</span>
          </p>
        </div>
      )}

      <KartuTelegram />

      <KartuDendaBelum />

      {r.perlu_tutup && (
        <div className="bg-danger/8 rounded-2xl border border-danger/40 shadow-soft p-4 flex flex-wrap items-center justify-between gap-3" data-testid="kas-perlu-tutup">
          <div>
            <p className="text-[10px] uppercase tracking-wider text-danger font-semibold">Belum ditutup</p>
            <p className="text-sm text-ink font-bold mt-0.5">
              Kas tunai {BULAN_ID(r.perlu_tutup)} belum ditutup
              {(r.belum_tutup || []).length > 1 && <span className="text-danger"> — dan {r.belum_tutup.length - 1} bulan lainnya</span>}
            </p>
            <p className="text-[11px] text-mocha leading-snug mt-0.5">
              {(r.belum_tutup || []).length > 1
                ? `Bulan yang belum ditutup: ${r.belum_tutup.map(BULAN_ID).join(' · ')}. Kerjakan dari yang paling lama supaya angkanya tidak tercampur.`
                : `Masih ada ${formatIDR(r.per_tempat?.cash || 0)} tunai yang belum masuk rekening. Setor dulu sisanya, lalu tutup — sisa yang tidak ada wujudnya dicatat sebagai utang atau prive bulan itu.`}
            </p>
          </div>
          <div className="flex flex-col gap-1.5 shrink-0">
            <button data-testid="kas-perlu-tutup-open" onClick={() => setTutupOpen(true)}
              className="inline-flex items-center gap-2 rounded-xl bg-danger text-white px-4 py-2.5 text-xs font-bold hover:bg-danger/90">
              <CalendarCheck size={14} /> Tutup {BULAN_ID(r.perlu_tutup)}
            </button>
            {r.perlu_tutup < (r.bulan_ini || '') && (
              <button data-testid="kas-lewati-bulan-lama" onClick={lewatiBulanLama}
                className="rounded-xl border border-border bg-white px-4 py-2 text-[11px] font-semibold text-mocha hover:bg-ivory">
                Lewati bulan sebelum {BULAN_ID(r.bulan_ini)}
              </button>
            )}
          </div>
        </div>
      )}

      {(r.tutup_dilewati || []).length > 0 && (
        <div className="bg-white rounded-2xl border border-border shadow-soft px-4 py-3 flex flex-wrap items-center justify-between gap-3" data-testid="kas-bulan-dilewati">
          <p className="text-[11px] text-mocha leading-snug">
            {r.tutup_dilewati.length} bulan lama sengaja dilewati dari Tutup Kas:{' '}
            {r.tutup_dilewati.map(BULAN_ID).join(' · ')}. Datanya tetap utuh, hanya pengingatnya dimatikan.
          </p>
          <button data-testid="kas-tagih-lagi" onClick={tagihSemuaBulan}
            className="rounded-xl border border-border bg-white px-3.5 py-2 text-[11px] font-bold text-brown hover:bg-ivory shrink-0">
            Tagih lagi
          </button>
        </div>
      )}

      <div className="bg-white rounded-2xl border border-border shadow-soft p-5" data-testid="kas-bisnis">
        <p className="text-[10px] uppercase tracking-wider text-mocha font-semibold">Kas Bisnis</p>
        <p className="font-display text-4xl md:text-5xl text-brown-dark mt-1 tracking-tight" data-testid="kas-bisnis-value">{formatIDR(r.kas_bisnis)}</p>
        <p className="text-xs text-mocha mt-2">
          Saldo awal {formatIDR(r.saldo_awal)}
          <span className="text-success"> + masuk {formatIDR(r.total_masuk)}</span>
          <span className="text-danger"> − keluar {formatIDR(r.total_keluar)}</span>
        </p>

        <div className="mt-4 pt-4 border-t border-border" data-testid="kas-tempat">
          <p className="text-[10px] uppercase tracking-wider text-mocha font-semibold mb-3">Uang ini tersimpan di 3 tempat</p>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-0">
            {TEMPAT.map((t, i) => (
              <div key={t.key} className={`px-0 md:px-5 ${i > 0 ? 'md:border-l border-border mt-4 md:mt-0 pt-4 md:pt-0 border-t md:border-t-0' : 'md:pl-0'}`}
                data-testid={`kas-tempat-${t.key}`}>
                <p className="text-sm text-ink font-bold flex items-center gap-2">
                  <span className={`w-2 h-2 rounded-full ${SOURCE_DOT[t.key]}`} />{SOURCE_L[t.key]}
                </p>
                <p className={`font-display text-2xl mt-1.5 tracking-tight ${(r.per_tempat?.[t.key] || 0) === 0 ? 'text-mocha' : 'text-brown-dark'}`}>
                  {formatIDR(r.per_tempat?.[t.key] || 0)}
                </p>
                <p className="text-[11px] text-mocha leading-snug mt-1">{t.note}</p>
              </div>
            ))}
          </div>
        </div>

        {r.uang_bebas < 0 && (
          <div className="mt-4 rounded-xl bg-danger/10 border border-danger/30 p-3">
            <p className="text-xs text-danger font-semibold">Uang titipan pelanggan sudah terpakai</p>
            <p className="text-[11px] text-mocha mt-0.5 leading-snug">
              Kas bisnis lebih kecil dari DP yang kamu pegang. Kalau ada pelanggan batal, uangnya belum tentu tersedia untuk dikembalikan.
            </p>
          </div>
        )}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-3" data-testid="kas-kartu-ringkas">
        <button type="button" onClick={() => setTitipanOpen(true)}
          className="text-left bg-white rounded-2xl border border-border shadow-soft p-4 hover:border-gold/60 hover:bg-ivory/40 transition"
          data-testid="kas-titipan">
          <div className="flex items-start justify-between gap-2">
            <p className="text-[10px] uppercase tracking-wider text-mocha font-semibold">Isi kas — titipan</p>
            <span className="text-[9.5px] uppercase tracking-wider text-gold-dark font-bold whitespace-nowrap">Lihat daftar →</span>
          </div>
          <p className="font-display text-2xl text-gold-dark mt-1.5 tracking-tight">{formatIDR(r.titipan)}</p>
          <p className="text-[11px] text-mocha leading-snug mt-1">
            Uang yang sudah diterima untuk acara yang belum terjadi — termasuk order yang sudah lunas.
            {Object.keys(r.titipan_per_bulan || {}).length > 0 && (
              <> <span className="text-brown-med">{Object.entries(r.titipan_per_bulan).map(([m, v]) => `${BULAN_ID(m)} ${formatIDR(v)}`).join(' · ')}</span></>
            )}
          </p>
        </button>

        <div className="bg-white rounded-2xl border border-border shadow-soft p-4" data-testid="kas-uang-bebas">
          <p className="text-[10px] uppercase tracking-wider text-mocha font-semibold">Isi kas — bebas dipakai</p>
          <p className={`font-display text-2xl mt-1.5 tracking-tight ${r.uang_bebas < 0 ? 'text-danger' : 'text-brown-dark'}`}>{formatIDR(r.uang_bebas)}</p>
          <p className="text-[11px] text-mocha leading-snug mt-1">Acaranya sudah lewat. Ini yang benar-benar milikmu.</p>
        </div>

        <div className="bg-white rounded-2xl border border-border shadow-soft p-4" data-testid="kas-piutang">
          <p className="text-[10px] uppercase tracking-wider text-mocha font-semibold">Di luar kas — piutang</p>
          <p className="font-display text-2xl text-danger mt-1.5 tracking-tight" data-testid="kas-piutang-value">{formatIDR(r.piutang)}</p>
          <p className="text-[11px] text-mocha leading-snug mt-1">Belum dibayar pelanggan. Tidak dihitung sebagai kas.</p>
        </div>

        <div className="bg-white rounded-2xl border border-border shadow-soft p-4" data-testid="kas-prive">
          <p className="text-[10px] uppercase tracking-wider text-mocha font-semibold">Dipakai pribadi — total</p>
          <p className="font-display text-2xl text-gold-dark mt-1.5 tracking-tight" data-testid="kas-prive-value">{formatIDR(r.pribadi_total)}</p>
          <p className="text-[11px] text-mocha leading-snug mt-1">
            Prive {formatIDR(r.prive)} · Utang {formatIDR(r.utang_ambil)}. Biaya operasional murni {formatIDR(r.biaya_operasional)}.
          </p>
        </div>
      </div>

      {(r.utang_ambil > 0 || r.utang_bayar > 0) && (
        <div className="bg-white rounded-2xl border border-gold/40 shadow-soft p-4" data-testid="kas-utang-pemilik">
          <p className="text-[10px] uppercase tracking-wider text-gold-dark font-semibold">Utang pemilik ke bisnis</p>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mt-2">
            <div>
              <p className="text-[11px] text-mocha">Total dipinjam</p>
              <p className="font-display text-xl text-brown-dark mt-0.5">{formatIDR(r.utang_ambil)}</p>
            </div>
            <div>
              <p className="text-[11px] text-mocha">Sudah dikembalikan</p>
              <p className="font-display text-xl text-success mt-0.5">{formatIDR(r.utang_bayar)}</p>
            </div>
            <div>
              <p className="text-[11px] text-mocha">Sisa utang</p>
              <p className={`font-display text-xl mt-0.5 ${r.utang_sisa > 0 ? 'text-danger' : 'text-success'}`} data-testid="kas-utang-sisa">{formatIDR(r.utang_sisa)}</p>
            </div>
          </div>
          <p className="text-[11px] text-mocha leading-snug mt-3">
            {r.utang_sisa > 0
              ? `Catat pengembaliannya lewat "+ Uang Masuk" dengan kategori "${CAT_BAYAR_UTANG}". Riwayat bulan lalu tidak berubah — yang turun hanya sisa utang ini.`
              : 'Lunas. Tidak ada utang pemilik yang tersisa.'}
          </p>
        </div>
      )}

      {(r.pinjaman_keluar > 0 || r.pinjaman_kembali > 0) && (
        <div className="bg-white rounded-2xl border border-gold/40 shadow-soft p-4" data-testid="kas-pinjaman">
          <p className="text-[10px] uppercase tracking-wider text-gold-dark font-semibold">Pinjaman ke orang lain</p>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mt-2">
            <div>
              <p className="text-[11px] text-mocha">Total dipinjamkan</p>
              <p className="font-display text-xl text-brown-dark mt-0.5" data-testid="kas-pinjaman-keluar">{formatIDR(r.pinjaman_keluar)}</p>
            </div>
            <div>
              <p className="text-[11px] text-mocha">Sudah dikembalikan</p>
              <p className="font-display text-xl text-success mt-0.5" data-testid="kas-pinjaman-kembali">{formatIDR(r.pinjaman_kembali)}</p>
            </div>
            <div>
              <p className="text-[11px] text-mocha">Belum kembali</p>
              <p className={`font-display text-xl mt-0.5 ${r.pinjaman_sisa > 0 ? 'text-danger' : 'text-success'}`} data-testid="kas-pinjaman-sisa">{formatIDR(r.pinjaman_sisa)}</p>
            </div>
          </div>
          {(r.pinjaman_rincian || []).length > 0 && (
            <ul className="mt-3 divide-y divide-gold/10 text-[12px]" data-testid="kas-pinjaman-rincian">
              {r.pinjaman_rincian.map((p, i) => (
                <li key={p.id || i} className="flex items-center justify-between gap-3 py-1.5">
                  <span className="text-mocha min-w-0 truncate">{formatDate(p.date)} · {p.notes || '-'}</span>
                  <span className={`shrink-0 font-semibold ${p.arah === 'kembali' ? 'text-success' : 'text-danger'}`}>
                    {p.arah === 'kembali' ? '+' : '-'}{formatIDR(p.amount)}
                  </span>
                </li>
              ))}
            </ul>
          )}
          <p className="text-[11px] text-mocha leading-snug mt-3">
            {r.pinjaman_sisa > 0
              ? `Saat dikembalikan, catat lewat "+ Uang Masuk" kategori "${CAT_PINJAMAN_KEMBALI}". Kalau uangnya sudah tercatat masuk dengan kategori lain, cukup ganti kategorinya (ikon pensil) — jangan tambah baris baru.`
              : 'Semua pinjaman sudah kembali.'}
          </p>
          <p className="text-[11px] text-mocha leading-snug mt-1">Bukan biaya dan bukan pendapatan — hanya uang usaha yang sedang dipinjam orang.</p>
        </div>
      )}

      {Object.keys(r.prive_per_bulan || {}).length > 0 && (
        <div className="bg-white rounded-2xl border border-border shadow-soft p-4" data-testid="kas-prive-bulanan">
          <p className="text-[10px] uppercase tracking-wider text-mocha font-semibold">Pemakaian pribadi per bulan</p>
          <p className="text-[11px] text-mocha leading-snug mt-0.5 mb-3">
            Berapa kas bisnis yang kamu pakai sendiri tiap bulan. Ini catatan sejarah — <b>tidak berubah</b> walau utangnya nanti kamu kembalikan.
          </p>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-2">
            {Object.entries(r.prive_per_bulan).map(([m, v]) => (
              <div key={m} className="rounded-xl bg-gold/8 border border-gold/30 px-3 py-2.5" data-testid={`kas-prive-${m}`}>
                <p className="text-[10px] uppercase tracking-wider text-gold-dark font-bold">{BULAN_ID(m)}</p>
                <p className="font-display text-lg text-brown-dark mt-0.5">{formatIDR(v.total ?? v)}</p>
                {v.total != null && (
                  <p className="text-[10px] text-mocha mt-1 leading-snug">
                    {v.utang > 0 && <>utang {formatIDR(v.utang)}</>}
                    {v.utang > 0 && v.prive > 0 && ' · '}
                    {v.prive > 0 && <>prive {formatIDR(v.prive)}</>}
                  </p>
                )}
                {r.bayar_per_bulan?.[m] > 0 && (
                  <p className="text-[10px] text-success font-semibold mt-1">dibayar {formatIDR(r.bayar_per_bulan[m])}</p>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {b.cash > 0 && (
        <div className="bg-white rounded-2xl border border-gold/40 shadow-soft p-4 flex items-baseline justify-between gap-3" data-testid="kas-wajib-setor">
          <div>
            <p className="text-[10px] uppercase tracking-wider text-gold-dark font-semibold">Kewajiban setor tunai</p>
            <p className="text-sm text-ink font-semibold mt-0.5">Uang tunai yang belum masuk rekening</p>
            <p className="text-[11px] text-mocha leading-snug">
              {data.tutup_tunai_terakhir
                ? `Tutup kas terakhir ${formatDate(data.tutup_tunai_terakhir.date)}. Setor sisanya, lalu tutup lagi di akhir bulan.`
                : 'Setor semuanya ke rekening operasional. Sisa yang tidak ada wujudnya, catat lewat Tutup Kas Tunai.'}
            </p>
          </div>
          <p className="font-display text-xl text-gold-dark whitespace-nowrap">{formatIDR(b.cash)}</p>
        </div>
      )}

      {r.tanpa_nota_jumlah > 0 && (
        <div className="bg-white rounded-2xl border border-danger/30 shadow-soft p-4 flex items-baseline justify-between gap-3" data-testid="kas-tanpa-nota">
          <div>
            <p className="text-[10px] uppercase tracking-wider text-danger font-semibold">Perlu dilengkapi</p>
            <p className="text-sm text-ink font-semibold mt-0.5">{r.tanpa_nota_jumlah} pengeluaran belum ada notanya</p>
            <p className="text-[11px] text-mocha leading-snug">Klik ikon kamera di kolom Nota pada baris yang bersangkutan.</p>
          </div>
          <p className="font-display text-xl text-danger whitespace-nowrap">{formatIDR(r.tanpa_nota_nilai)}</p>
        </div>
      )}

      <div className="bg-white rounded-2xl border border-border shadow-soft p-4 flex flex-wrap items-center justify-between gap-3" data-testid="kas-rekon-bar">
        <div>
          <p className="text-[10px] uppercase tracking-wider text-mocha font-semibold">Pencocokan Rekening</p>
          {data.rekonsiliasi_terakhir ? (
            <p className="text-sm mt-0.5">
              Terakhir {formatDate(data.rekonsiliasi_terakhir.date)} — selisih{' '}
              <b className={data.rekonsiliasi_terakhir.selisih === 0 ? 'text-success' : 'text-danger'}>
                {formatIDR(data.rekonsiliasi_terakhir.selisih)}
              </b>
            </p>
          ) : (
            <p className="text-sm text-mocha mt-0.5">Belum pernah dicocokkan dengan rekening.</p>
          )}
        </div>
        <button data-testid="kas-rekon-open" onClick={() => setRekonOpen(true)}
          className="inline-flex items-center gap-2 rounded-xl bg-brown text-white px-4 py-2.5 text-xs font-bold hover:bg-brown-med">
          <Scale size={14} /> Cocokkan Rekening
        </button>
      </div>

      <div className="bg-white rounded-2xl border border-border shadow-soft p-4" data-testid="kas-today">
        <p className="text-[10px] uppercase tracking-wider text-mocha font-semibold mb-2">Hari Ini</p>
        <div className="flex gap-5 flex-wrap text-sm">
          <span>Masuk <b className="text-success" data-testid="kas-today-in">{formatIDR(data.today.in)}</b></span>
          <span>Keluar <b className="text-danger" data-testid="kas-today-out">{formatIDR(data.today.out)}</b></span>
          <span>Net <b className={data.today.net >= 0 ? 'text-success' : 'text-danger'} data-testid="kas-today-net">{formatIDR(data.today.net)}</b></span>
        </div>
      </div>

      <KasPerAkun versi={data} onPerluCek={() => setFilters({ ...filters, perlu_cek: '1' })} />

      <div className="flex gap-2 flex-wrap" data-testid="kas-actions">
        <button data-testid="kas-add-in" onClick={() => setDialog('in')} className="inline-flex items-center gap-1.5 rounded-xl bg-success text-white px-4 py-2.5 text-xs font-bold hover:bg-success/90"><Plus size={14} /> Uang Masuk</button>
        <button data-testid="kas-add-out" onClick={() => setDialog('out')} className="inline-flex items-center gap-1.5 rounded-xl bg-danger text-white px-4 py-2.5 text-xs font-bold hover:bg-danger/90"><Minus size={14} /> Pengeluaran</button>
        <button data-testid="kas-add-transfer" onClick={() => setDialog('transfer')} className="inline-flex items-center gap-1.5 rounded-xl bg-gold text-white px-4 py-2.5 text-xs font-bold hover:bg-gold-dark"><ArrowLeftRight size={14} /> Transfer Antar Kas</button>
        <button data-testid="kas-scan-nota" onClick={() => setDialog('scan-nota')} className="inline-flex items-center gap-1.5 rounded-xl bg-brown text-white px-4 py-2.5 text-xs font-bold hover:bg-brown-med"><ScanLine size={14} /> Scan Nota</button>
        <button data-testid="kas-scan-transfer" onClick={() => setDialog('scan-transfer')} className="inline-flex items-center gap-1.5 rounded-xl bg-brown text-white px-4 py-2.5 text-xs font-bold hover:bg-brown-med"><ScanLine size={14} /> Scan Bukti Transfer</button>
        <button data-testid="kas-tutup-tunai" onClick={() => setTutupOpen(true)} className="inline-flex items-center gap-1.5 rounded-xl bg-brown-dark text-white px-4 py-2.5 text-xs font-bold hover:bg-brown"><CalendarCheck size={14} /> Tutup Kas Tunai</button>
        <button data-testid="kas-export-all" disabled={exporting} onClick={() => doExport('')} className="inline-flex items-center gap-1.5 rounded-xl bg-white border border-border text-brown px-4 py-2.5 text-xs font-bold hover:bg-ivory disabled:opacity-60"><Download size={14} /> Export Excel</button>
      </div>

      <div className="bg-white rounded-2xl border border-border shadow-soft p-4 flex flex-wrap gap-2 items-end" data-testid="kas-filters">
        <div>
          <label className="text-[10px] uppercase tracking-wider text-mocha font-semibold">Dari</label>
          <input data-testid="kas-filter-start" type="date" value={filters.start} onChange={(e) => setFilters({ ...filters, start: e.target.value })}
            className="block mt-1 rounded-xl border border-input bg-ivory/50 px-3 py-2 text-xs outline-none" />
        </div>
        <div>
          <label className="text-[10px] uppercase tracking-wider text-mocha font-semibold">Sampai</label>
          <input data-testid="kas-filter-end" type="date" value={filters.end} onChange={(e) => setFilters({ ...filters, end: e.target.value })}
            className="block mt-1 rounded-xl border border-input bg-ivory/50 px-3 py-2 text-xs outline-none" />
        </div>
        <select data-testid="kas-filter-direction" value={filters.direction} onChange={(e) => setFilters({ ...filters, direction: e.target.value })}
          className="rounded-xl border border-input bg-ivory/50 px-3 py-2 text-xs outline-none">
          <option value="">Semua Jenis</option><option value="in">Uang Masuk</option><option value="out">Uang Keluar</option><option value="transfer">Transfer Internal</option>
        </select>
        <select data-testid="kas-filter-source" value={filters.source} onChange={(e) => setFilters({ ...filters, source: e.target.value })}
          className="rounded-xl border border-input bg-ivory/50 px-3 py-2 text-xs outline-none">
          <option value="">Semua Sumber</option>{Object.entries(SOURCE_L).map(([v, l]) => <option key={v} value={v}>{l}</option>)}
        </select>
        <div className="relative flex-1 min-w-[140px]">
          <input data-testid="kas-filter-q" value={filters.q} onChange={(e) => setFilters({ ...filters, q: e.target.value })} placeholder="Cari toko, keterangan, customer, kategori, no. order…"
            className="w-full rounded-xl border border-input bg-ivory/50 pl-3 pr-9 py-2 text-xs outline-none" />
          <HapusCari show={!!filters.q} onClear={() => setFilters({ ...filters, q: '' })} testid="kas-filter-q-clear" />
        </div>
        {(r.akun_perlu_cek > 0 || filters.perlu_cek) && (
          <button type="button" data-testid="kas-filter-perlu-cek"
            onClick={() => setFilters({ ...filters, perlu_cek: filters.perlu_cek ? '' : '1' })}
            className={`rounded-xl border px-3 py-2 text-xs font-bold ${filters.perlu_cek ? 'bg-amber-500 text-white border-amber-500' : 'bg-amber-50 text-amber-900 border-amber-200'}`}>
            ⚠️ Perlu dicek{r.akun_perlu_cek ? ` (${r.akun_perlu_cek})` : ''}
          </button>
        )}
      </div>

      {ringkasCari && (
        <div className="bg-white rounded-2xl border border-border shadow-soft px-4 py-3 flex flex-wrap items-center justify-between gap-2" data-testid="kas-cari-ringkas">
          <p className="text-sm">
            <b>{ringkasCari.jumlah} transaksi</b> cocok dengan &ldquo;{filters.q.trim()}&rdquo;
            {ringkasCari.jumlah > 0 && (
              <>
                {' '}· <span className="text-success font-semibold" data-testid="kas-cari-masuk">Masuk {formatIDR(ringkasCari.masuk)}</span>
                {' '}· <span className="text-danger font-semibold" data-testid="kas-cari-keluar">Keluar {formatIDR(ringkasCari.keluar)}</span>
              </>
            )}
            {ringkasCari.tidakDihitung > 0 && (
              <span className="block text-[11px] text-mocha mt-0.5">{ringkasCari.tidakDihitung} baris void/draf tidak dihitung ke jumlah di atas.</span>
            )}
          </p>
          <button type="button" data-testid="kas-cari-hapus" onClick={() => setFilters({ ...filters, q: '' })}
            className="rounded-full bg-ivory border border-border text-mocha px-3 py-1 text-[11px] font-bold hover:bg-white">
            Hapus pencarian
          </button>
        </div>
      )}

      {barisVoid.length > 0 && (
        <div className="bg-white rounded-2xl border border-border shadow-soft px-4 py-3 flex flex-wrap items-center justify-between gap-3" data-testid="kas-void-bar">
          <div>
            <p className="text-sm text-ink">
              <b>{barisVoid.length} baris void</b>{' '}
              <span className="text-mocha">{tampilVoid ? 'sedang ditampilkan.' : 'disembunyikan dari tabel.'}</span>
            </p>
            <p className="text-[11px] text-mocha leading-snug mt-0.5">
              Baris void tidak memengaruhi saldo sama sekali — hanya sisa koreksi lama. Sekarang koreksi cukup pakai ikon pensil.
            </p>
          </div>
          <div className="flex gap-2 shrink-0">
            <button data-testid="kas-void-toggle" onClick={() => setTampilVoid((v) => !v)}
              className="rounded-xl border border-border bg-white px-3.5 py-2 text-xs font-bold text-brown hover:bg-ivory">
              {tampilVoid ? 'Sembunyikan' : 'Tampilkan'}
            </button>
            <button data-testid="kas-void-hapus" onClick={() => setHapusVoidOpen(true)}
              className="rounded-xl border border-danger/40 bg-white px-3.5 py-2 text-xs font-bold text-danger hover:bg-danger/10">
              Hapus permanen
            </button>
          </div>
        </div>
      )}

      <div className="bg-white rounded-2xl border border-border shadow-soft overflow-x-auto" data-testid="kas-table-card">
        <table className="w-full text-sm min-w-[900px]">
          <thead>
            <tr className="text-left text-[10px] uppercase tracking-wider text-mocha border-b border-border">
              <th className="px-3 py-3 font-bold">Tanggal</th>
              <th className="px-3 py-3 font-bold">Jenis</th>
              <th className="px-3 py-3 font-bold">Kategori &amp; Keterangan</th>
              <th className="px-3 py-3 font-bold">Sumber Dana</th>
              <th className="px-3 py-3 font-bold">Customer</th>
              <th className="px-3 py-3 font-bold text-right">Nominal</th>
              <th className="px-3 py-3 font-bold text-right" title={adaFilter ? 'Saldo disembunyikan saat memakai pencarian atau filter, karena dihitung hanya dari baris yang tampil' : undefined}>Saldo{adaFilter ? '*' : ''}</th>
              <th className="px-3 py-3 font-bold">Status</th>
              <th className="px-3 py-3 font-bold text-center">Nota</th>
              <th className="px-3 py-3 font-bold text-right">Aksi</th>
            </tr>
          </thead>
          <tbody>
            {barisTampil.length === 0 && (
              <tr><td colSpan={10} className="px-4 py-8 text-center text-sm text-mocha" data-testid="kas-empty">Belum ada transaksi kas.</td></tr>
            )}
            {barisTampil.map((r) => (
              <tr key={r.id} data-testid={`kas-row-${r.id.slice(0, 13)}`} className={`border-b border-border/60 ${r.status === 'Void' ? 'opacity-50' : 'hover:bg-ivory/60'}`}>
                <td className="px-3 py-2.5 whitespace-nowrap text-mocha">{formatDate(r.date)}</td>
                <td className="px-3 py-2.5"><span className={`rounded-full px-2.5 py-0.5 text-[10px] font-bold ${DIR_CLS[r.direction]}`}>{DIR_L[r.direction]}</span></td>
                <td className="px-3 py-2.5 align-top">
                  <span><Tandai teks={r.category} kata={kataCari} /></span>
                  {r.direction === 'out' && r.status !== 'Void' && (r.akun_perlu_cek || infoAkun(r.category).lama) && (
                    <button type="button" data-testid={`kas-cek-akun-${r.id.slice(0, 13)}`} onClick={() => setEditFor(r)}
                      title="Akun belum pasti — ketuk untuk membetulkan"
                      className="ml-1.5 rounded-full bg-amber-100 text-amber-900 px-1.5 py-0.5 text-[9px] font-bold align-middle">cek akun</button>
                  )}
                  {r.order_number ? <span className="text-[10px] text-brown font-bold ml-1.5"><Tandai teks={r.order_number} kata={kataCari} /></span> : ''}
                  {r.notes ? (
                    <span data-testid={`kas-notes-${r.id}`} title={r.notes}
                      className="block text-[11px] text-mocha leading-snug mt-0.5 max-w-[260px]"
                      style={{ display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>
                      <Tandai teks={r.notes} kata={kataCari} />
                    </span>
                  ) : null}
                </td>
                <td className="px-3 py-2.5 whitespace-nowrap">
                  <SrcTag s={r.source} />
                  {r.direction === 'transfer' && <><span className="text-mocha mx-1">→</span><SrcTag s={r.target_source} /></>}
                </td>
                <td className="px-3 py-2.5 font-semibold">{r.customer_name ? <Tandai teks={r.customer_name} kata={kataCari} /> : '-'}</td>
                <td className={`px-3 py-2.5 text-right font-bold whitespace-nowrap ${r.direction === 'out' ? 'text-danger' : r.direction === 'in' ? 'text-success' : 'text-gold-dark'}`}>
                  {r.direction === 'out' ? '-' : r.direction === 'in' ? '+' : ''}{formatIDR(r.amount)}
                </td>
                <td className="px-3 py-2.5 text-right text-mocha whitespace-nowrap">{!adaFilter && r.saldo != null ? formatIDR(r.saldo) : '-'}</td>
                <td className="px-3 py-2.5"><span className={`text-[10px] font-bold ${r.status === 'Void' ? 'text-danger' : 'text-success'}`}>{r.status}</span></td>
                <td className="px-3 py-2.5 text-center">
                  {r.payment_id || r.direction === 'transfer' ? (
                    <span className="text-mocha">-</span>
                  ) : r.has_photo ? (
                    <button data-testid={`kas-nota-view-${r.id.slice(0, 8)}`} onClick={() => setNotaFor(r)}
                      className="p-1.5 rounded-lg text-success hover:bg-success/10" title="Lihat nota">
                      <Eye size={15} />
                    </button>
                  ) : (
                    <button data-testid={`kas-nota-add-${r.id.slice(0, 8)}`} onClick={() => setNotaFor(r)}
                      className={`p-1.5 rounded-lg hover:bg-gold/10 ${r.no_receipt_reason ? 'text-mocha' : 'text-danger'}`}
                      title={r.no_receipt_reason ? `Tanpa nota — ${r.no_receipt_reason}` : 'Belum ada nota — klik untuk foto'}>
                      <Camera size={15} />
                    </button>
                  )}
                </td>
                <td className="px-3 py-2.5 text-right whitespace-nowrap">
                  {!r.payment_id && r.status !== 'Void' && (
                    <>
                      <button data-testid={`kas-edit-${r.id.slice(0, 8)}`} onClick={() => setEditFor(r)} className="p-1.5 rounded-lg text-brown hover:bg-brown/10" title="Koreksi nilai">
                        <Pencil size={14} />
                      </button>
                      <button data-testid={`kas-void-${r.id.slice(0, 8)}`} onClick={() => setVoidFor(r)} className="p-1.5 rounded-lg text-danger hover:bg-danger/10" title="Void (batalkan seluruh baris)">
                        <Ban size={14} />
                      </button>
                    </>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {barisTampil.length > 0 && (() => {
        const tk = susunTotalKas(barisTampil, filters, SOURCE_L);
        return <TotalBar testid="kas-total" judul={tk.judul} items={tk.items} catatan={tk.catatan} />;
      })()}

      <RekonDialog open={rekonOpen} onClose={() => setRekonOpen(false)} ringkasan={r} onSaved={load} />
      <AlertDialog open={hapusVoidOpen} onOpenChange={setHapusVoidOpen}>
        <AlertDialogContent data-testid="kas-void-hapus-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle className="font-display text-brown-dark">Hapus {barisVoid.length} baris void?</AlertDialogTitle>
            <AlertDialogDescription>
              Baris ini dihapus permanen dari tabel kas. Saldo tidak berubah sedikit pun — baris void memang tidak pernah dihitung.
              Salinannya tetap disimpan di database (koleksi <b>void_terhapus</b>) kalau suatu saat perlu ditelusuri.
              Foto nota yang menempel pada baris itu ikut terhapus.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="kas-void-hapus-batal">Batal</AlertDialogCancel>
            <AlertDialogAction data-testid="kas-void-hapus-ya" onClick={hapusSemuaVoid} disabled={hapusBusy}
              className="bg-danger hover:bg-danger/90">
              {hapusBusy ? 'Menghapus…' : 'Ya, hapus permanen'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <TitipanDialog open={titipanOpen} onClose={() => setTitipanOpen(false)} rincian={r.titipan_rincian || []} total={r.titipan} />
      <TutupTunaiDialog open={tutupOpen} onClose={() => setTutupOpen(false)} cashSistem={b.cash || 0} perluTutup={r.perlu_tutup} belumTutup={r.belum_tutup || []} onSaved={load} />
      <EditEntryDialog entry={editFor} onClose={() => setEditFor(null)} onSaved={load} />
      <NotaDialog entry={notaFor} onClose={() => setNotaFor(null)} onSaved={load} />
      <EntryDialog open={dialog === 'in'} onClose={() => setDialog(null)} direction="in" onSaved={load} />
      <EntryDialog open={dialog === 'out'} onClose={() => setDialog(null)} direction="out" onSaved={load} />
      <TransferDialog open={dialog === 'transfer'} onClose={() => setDialog(null)} onSaved={load} />
      <ScanDialog open={dialog === 'scan-nota'} onClose={() => setDialog(null)} mode="nota" onSaved={load} />
      <ScanDialog open={dialog === 'scan-transfer'} onClose={() => setDialog(null)} mode="transfer" onSaved={load} />

      <Dialog open={saldoOpen} onOpenChange={setSaldoOpen}>
        <DialogContent data-testid="kas-saldo-dialog" className="bg-ivory max-w-sm">
          <DialogHeader><DialogTitle className="font-display text-brown-dark">Saldo Awal Kas</DialogTitle></DialogHeader>
          <div className="space-y-2.5">
            {Object.entries(SOURCE_L).map(([k, l]) => (
              <div key={k}>
                <label className="text-[11px] uppercase tracking-wider text-mocha font-semibold">{l}</label>
                <MoneyInput data-testid={`saldo-awal-${k}`} value={saldoForm[k]} onChange={(v) => setSaldoForm({ ...saldoForm, [k]: v })}
                  className="mt-1 w-full rounded-xl border border-input bg-white px-3 py-2.5 text-sm outline-none" />
              </div>
            ))}
            <button data-testid="saldo-awal-save" onClick={saveSaldoAwal}
              className="w-full rounded-xl bg-brown text-white py-3 text-sm font-bold hover:bg-brown-med">Simpan</button>
          </div>
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!voidFor} onOpenChange={() => setVoidFor(null)}>
        <AlertDialogContent data-testid="kas-void-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle className="font-display text-danger">Void Transaksi?</AlertDialogTitle>
            <AlertDialogDescription>
              Transaksi {voidFor?.category} {formatIDR(voidFor?.amount || 0)} akan di-void. Data tidak dihapus permanen — riwayat perubahan tetap tersimpan.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="kas-void-cancel">Batal</AlertDialogCancel>
            <AlertDialogAction data-testid="kas-void-confirm" onClick={doVoid} className="bg-danger hover:bg-danger/90">Ya, Void</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
